/**
 * Staff directory — teaching and non-teaching — from the institute's employee
 * record (BIET_RESOURCE/Staff/employees.xlsx, 869 rows) joined to its photo set.
 *
 * Only the 394 rows flagged `Photo Found = Yes` appear here; each one's image was
 * verified present before ingest, so every record below has a photo. Three have
 * since been removed (2026-11-03) — Praveen Kumar B H and Vaibhav Deshpande, the
 * MBA's only two people, and Raghavendra N T, who has no department — because
 * published content does not carry them and the site no longer lists people it
 * cannot source.
 *
 * Ordering is teaching first, then non-teaching, and within each by designation
 * seniority then name — see TEACHING_RANK / NON_TEACHING_RANK in the generator.
 * "Teaching" comes from the record's own Category column, which splits cleanly:
 * no designation appears on both sides of it.
 *
 * The source spreadsheet also holds personal email addresses and mobile numbers.
 * Those are deliberately NOT carried here — unlike the alumni coordinators, this
 * is an internal record rather than something the institute already publishes.
 *
 * Department codes are reproduced as recorded, with two deliberate exceptions.
 * Only expansions we are certain of are given a `deptName`; the rest keep the
 * bare code.
 *
 * The second is Pradeep N, whom the spreadsheet files under CS. He heads Data
 * Science — departments.ts has DS naming him as its head and CS naming
 * Nirmala C R — so he is moved to "CD", which is this record's own code for
 * Data Science (departments.ts maps DS to staffCode "CD", CD to "CG", CSBS to
 * "CB" and AIML to "AI"). Setting a literal "DS" here would look right and
 * quietly drop him from every department roster, since no department maps to
 * that code.
 *
 * The first exception is the spreadsheet's "FY" (First Year) code, which lumps all
 * four basic-science departments into one bucket. Those 39 people are split
 * back out to PHY / CHE / MAT / SH here, so the directory groups them the same
 * way Academics does. 31 of the splits are read off the roster each department
 * publishes on its own page (see `publishedFaculty` in departments.ts). The
 * remaining eight — Chandrappa K G, Rajprakash H G, Basavaraj Hugar,
 * Nagarathnamma K G, Pradeepkumar J, Rekha M B, Annapurneshwari P and
 * P Rudreshi — appear on no published department page and are placed in
 * Humanities, which publishes no roster of its own. That placement is an
 * editorial assignment, not a sourced fact.
 *
 * Photo dimensions are kept as recorded: <Figure> caps its srcSet ladder at the
 * source's own width so the CDN is never asked to upscale, and they let a
 * print-quality portrait be told from a thumbnail. Some are as small as 86x107.
 *
 * The CS roster was reconciled against the department's own published faculty
 * list (2026-08-10): Amith Shekhar C, Shankarayya Shasthri, S Anjanappa and
 * Kavya M were removed outright (not present on that list, and not known to
 * belong to any other department); Mohamed Muthahir R and Vinutha K P were
 * moved in from where the spreadsheet had filed them (MCA, and "SCE" — a
 * department code that appears nowhere else in this file). The 390 rows this
 * leaves are one under the "394 rows" ingest count above. The CS block's
 * internal order was also changed to match the department's published
 * sequence, rather than the designation-seniority-then-name order the rest
 * of the file follows.
 *
 * IS was reconciled the same way (2026-08-10): Ashoka K and Poornima B (the
 * department's own head, per departments.ts) were both moved in from orphan
 * codes ("MCS" and "MBA") found nowhere else here; Poornima B's photo was
 * also swapped for her HOD portrait, the confirmed one. Parveen Banu N was
 * added even though she is absent from the spreadsheet's photographed 394 —
 * the department names her directly. Her photo did not come from the HR
 * spreadsheet either: it was found in the IS gallery sweep, filed under her
 * own name and excluded there as a portrait, and is used here instead.
 *
 * CB (formerly CSBS — see placements.ts) was reconciled the same way
 * (2026-08-10): Spoorti Admani was removed, not present on the department's
 * own faculty list, and the CB block was reordered to match it. Rahul Patil
 * G B (SDA) was removed on request shortly after.
 *
 * CD (staffCode "CG") was reconciled the same way (2026-08-10): Chetana
 * Prakash's designation was corrected to "Professor & Head" (she is CD's
 * confirmed head, per departments.ts) and Shadaksharaiah C's "& Head" was
 * dropped, matching the same real-head-vs-staff.ts-designation mismatch
 * already fixed once for CS and once for IS. Sreenivasa B R was moved in
 * from "MAD", a code shared by four other people at the time — not an
 * orphan/typo the way "SCE" and "MCS" were, just a catch-all bucket needing
 * per-person reconciliation against each real department's own roster (see
 * the AI note below, which claimed three more of them). Confirmed for
 * Sreenivasa by his listing on CS's Board of Studies as CD faculty. Ksheera
 * P B was added despite no photo anywhere (spreadsheet or gallery sweeps).
 * Pramod H was removed on request, then DS's own roster (below) placed him
 * there instead — not a CD person at all, just misfiled under "CG" (CD's
 * staffCode) rather than "CD" (DS's staffCode, an unrelated coincidence of
 * naming — see the Pradeep N note above). The CD block was reordered to
 * match the department's own published sequence.
 *
 * DS (staffCode "CD" — see the Pradeep N note above for why) was
 * reconciled the same way (2026-08-10): Pramod H moved in from CD/"CG"
 * (recovered from git history, since his record had just been deleted
 * outright there), Nagaraj R's double-space typo fixed, and the block
 * reordered to match the department's published sequence.
 *
 * AI (formerly AIML — see placements.ts) was reconciled the same way
 * (2026-08-10): Azizkhan F Pathan and K T Ramu were removed, not present on
 * the department's own list. Puneeth B H, Sushma B Baraker and Chethan K P
 * R were moved in from "MAD" — three of the four people Sreenivasa B R's
 * note above mentions sharing that code; Sandeep G S, the fourth, stayed
 * filed there, department unconfirmed, until his removal (2026-10-09, see
 * the end of this note). Shanmukha N's double-space typo was fixed. The
 * block was reordered to match the department's published sequence.
 *
 * ME was reconciled the same way (2026-08-10): B S Umesh, M Krishnamurthy
 * and Deviprasad N Mirashi were removed, not present on the department's
 * own list. D E Umesh's double-space typo was fixed. The teaching block was
 * reordered to match the department's published sequence; non-teaching
 * staff were left in their existing order, not covered by that list.
 *
 * CV was reconciled the same way (2026-08-10): Rahul Patil and
 * Revanasiddappa M were removed — both carried a "& Head" designation
 * despite neither being CV's actual head (Chidananda G, confirmed by
 * departments.ts and by the department's own list) — along with
 * Chandrashekhar A R, also absent from that list. Suresh S, Raghu M E and
 * Basavanagowda S N were moved in from "CEE" and "CSE", two more
 * PG-programme-specific spreadsheet codes (Environmental Engineering and
 * Structural Engineering respectively, both CV's own PG programmes) rather
 * than orphans or typos. Supreeth had no department code at all and was
 * given one. R S Chikkangoudar was added new, with no HR-spreadsheet
 * record — his photo came from the CV gallery sweep, filed under his own
 * name. The teaching block was reordered to match the department's
 * published sequence.
 *
 * BT was reconciled the same way (2026-08-10): Ganesh G Tilve, Vismaya R
 * Prasad and Maruthi Rajanahalli were removed, not present on the
 * department's own list. Rahul Patil G B — removed from CB earlier in this
 * same reconciliation pass — turned out to belong here instead: BT's list
 * names him as Instructor, so his record (and photo, never deleted from
 * storage) was restored under BT rather than CB. The teaching and
 * non-teaching blocks were both reordered to match the department's
 * published sequence.
 *
 * EE was reconciled the same way (2026-08-11): Shekhappa G Ankaliki, Joysun
 * Dsouza, Shreeshaila B Angadi and A Manjappa were removed, not present on
 * the department's own list. The teaching block was reordered to match the
 * department's published sequence; non-teaching staff were left in their
 * existing order, not covered by that list.
 *
 * CH was reconciled the same way (2026-08-11): Gururaj Rao Desai (retired
 * and relieved 31-07-2025), Manishkumar A Patil and Rajashekhara S (both
 * flagged by the department as not this institute's own staff) were
 * removed, on request.
 *
 * MAT was reconciled the same way (2026-08-11): Kavya P M (relieved
 * 31-07-2026) and C Amaresh were removed, on request. Pradeepkumar J, Rekha
 * M B, Nagarathnamma K G and P Rudreshi were requested as new additions but
 * already existed here, misfiled under "SH" (Humanities) — same names, same
 * designations, same photos — so they were reassigned to MAT rather than
 * duplicated. SH's own roster is now empty as a result; its department page
 * carries little else (see departments.ts's "sh" entry), so this wasn't
 * flagged as its own reconciliation.
 *
 * Removed on request (2026-10-09): Sandeep G S ("MAD"), M E Ashalatha,
 * Manohara N V and Durugappa (EC), and H N Ravikumar (ME). Sandeep and the
 * three EC people had been removed from webadmin, but mergedFaculty.ts keeps
 * every staff.ts person webadmin doesn't publish, so they stayed on the site
 * until their records here went too. H N Ravikumar was never in webadmin.
 */
import type { Media } from "@zivin-labs/site-content";

export interface StaffMember {
  /** Stable key, also the photo filename. */
  slug: string;
  name: string;
  designation: string;
  /** Department code exactly as recorded; empty when unassigned. */
  deptCode: string;
  /** Expanded name where the code is known, else the bare code. */
  deptName: string;
  teaching: boolean;
  photo: Media;
}

/** Monogram fallback, e.g. "Vijaya Kumar C S" -> "VK". */
export function initials(name: string): string {
  const w = name.replace(/[^A-Za-z\s]/g, " ").split(/\s+/).filter(Boolean);
  if (!w.length) return "\u2022";
  return (w.length === 1 ? w[0].slice(0, 2) : w[0][0] + w[1][0]).toUpperCase();
}

export const STAFF: StaffMember[] = [
  {
    slug: "h-b-aravind",
    name: "H B Aravind",
    designation: "Professor and Principal",
    deptCode: "CV",
    deptName: "Civil Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/h-b-aravind.jpg", alt: "H B Aravind, Professor and Principal", width: 294, height: 316 },
  },
  {
    slug: "anjaneya-l-h",
    name: "Anjaneya L H",
    designation: "Professor & Head",
    deptCode: "EE",
    deptName: "Electrical & Electronics Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/anjaneya-l-h.jpg", alt: "Anjaneya L H, Professor & Head", width: 1991, height: 2560 },
  },
  {
    slug: "b-poornima",
    name: "Poornima B",
    // No longer "& Head" — Dr. Vinutha H P became IS's head (2026-08-10),
    // dual-appointed alongside CSBS/CB. Assumed to remain IS faculty; not
    // confirmed she stays in the department at all past the handover.
    designation: "Professor",
    deptCode: "IS",
    deptName: "Information Science & Engineering",
    teaching: true,
    // Same frame as her HOD portrait in departments.ts (is-hod-01.jpg) — the
    // old b-poornima.jpg was tied to the wrong-department record and never
    // confirmed as actually her.
    photo: { url: "sites/biet/dept-hod/is-hod-01.jpg", alt: "Poornima B, Professor", width: 1991, height: 2560 },
  },
  {
    slug: "c-m-kalleshappa",
    name: "C M Kalleshappa",
    designation: "Professor & Head",
    deptCode: "CH",
    deptName: "Chemical Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/c-m-kalleshappa.jpg", alt: "C M Kalleshappa, Professor & Head", width: 353, height: 530 },
  },
  {
    slug: "chidananda-g",
    name: "Chidananda G",
    designation: "Professor & Head",
    deptCode: "CV",
    deptName: "Civil Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/chidananda-g.jpg", alt: "Chidananda G, Professor & Head", width: 1991, height: 2560 },
  },
  {
    slug: "suresh-s",
    name: "Suresh S",
    designation: "Professor",
    deptCode: "CV",
    deptName: "Civil Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/suresh-s.jpg", alt: "Suresh S, Professor", width: 1991, height: 2560 },
  },
  {
    slug: "dinesh-y-n",
    name: "Dinesh Y N",
    designation: "Professor & Head",
    deptCode: "TX",
    deptName: "Textile Technology",
    teaching: true,
    photo: { url: "sites/biet/faculty/dinesh-y-n.jpg", alt: "Dinesh Y N, Professor & Head", width: 853, height: 1280 },
  },
  {
    slug: "g-manavendra",
    name: "G Manavendra",
    designation: "Professor & Head",
    deptCode: "ME",
    deptName: "Mechanical Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/g-manavendra.jpg", alt: "G Manavendra, Professor & Head", width: 270, height: 350 },
  },
  {
    slug: "g-s-sunitha",
    name: "G S Sunitha",
    designation: "Professor & Head",
    deptCode: "EC",
    deptName: "Electronics & Communication Engineering",
    teaching: true,
    // The employee photo set's own file for her is 100x129 — visibly soft
    // anywhere it is rendered above thumbnail size, such as the dean card on
    // the About page. She heads ECE, whose department page publishes a
    // full-size portrait of the same person, so this record points there
    // instead. The only row in this file whose image comes from outside the
    // faculty set; sites/biet/faculty/g-s-sunitha.jpg is now unreferenced.
    photo: { url: "sites/biet/dept-hod/ec-hod-01.jpg", alt: "G S Sunitha, Professor & Head", width: 1991, height: 2560 },
  },
  {
    slug: "n-s-basavarajappa",
    name: "N S Basavarajappa",
    designation: "Professor & Head",
    deptCode: "MAT",
    deptName: "Mathematics",
    teaching: true,
    photo: { url: "sites/biet/faculty/n-s-basavarajappa.jpg", alt: "N S Basavarajappa, Professor & Head", width: 1063, height: 1192 },
  },
  {
    slug: "nirmala-c-r",
    name: "Nirmala C R",
    designation: "Professor & Head",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/nirmala-c-r.jpg", alt: "Nirmala C R, Professor & Head", width: 2560, height: 2560 },
  },
  {
    slug: "pradeep-n",
    name: "Pradeep N",
    designation: "Professor & Head",
    // The employee record files him under CS; he heads Data Science. "CD" is
    // that record's own code for Data Science — departments.ts maps DS to
    // staffCode "CD" — so this is the value that puts him on the DS roster
    // alongside its other ten staff. See the note in the file header.
    deptCode: "CD",
    deptName: "Computer Science & Engineering (Data Science)",
    teaching: true,
    photo: { url: "sites/biet/faculty/pradeep-n.jpg", alt: "Pradeep N, Professor & Head", width: 1921, height: 2553 },
  },
  {
    slug: "prashantha-g-r",
    name: "Prashantha G R",
    designation: "Associate Professor",
    deptCode: "CD",
    deptName: "Computer Science & Engineering (Data Science)",
    teaching: true,
    photo: { url: "sites/biet/faculty/prashantha-g-r.jpg", alt: "Prashantha G R, Associate Professor", width: 389, height: 357 },
  },
  {
    slug: "s-b-mallikarjuna",
    name: "S B Mallikarjuna",
    designation: "Professor & Head",
    deptCode: "AI",
    deptName: "AI & Machine Learning",
    teaching: true,
    photo: { url: "sites/biet/faculty/s-b-mallikarjuna.jpg", alt: "S B Mallikarjuna, Professor & Head", width: 2104, height: 2560 },
  },
  {
    slug: "s-kumarappa",
    name: "S Kumarappa",
    designation: "Professor",
    deptCode: "ME",
    deptName: "Mechanical Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/s-kumarappa.jpg", alt: "S Kumarappa, Professor", width: 2032, height: 1839 },
  },
  {
    slug: "anilakumar-c-p",
    name: "Anilakumar C P",
    designation: "Professor",
    deptCode: "CV",
    deptName: "Civil Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/anilakumar-c-p.jpg", alt: "Anilakumar C P, Professor", width: 2030, height: 2424 },
  },
  {
    slug: "ashoka-k",
    name: "Ashoka K",
    designation: "Professor",
    deptCode: "IS",
    deptName: "Information Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/ashoka-k.jpg", alt: "Ashoka K, Professor", width: 272, height: 354 },
  },
  {
    slug: "chetana-prakash",
    name: "Chetana Prakash",
    designation: "Professor & Head",
    deptCode: "CG",
    deptName: "Computer Science & Design",
    teaching: true,
    photo: { url: "sites/biet/faculty/chetana-prakash.jpg", alt: "Chetana Prakash, Professor & Head", width: 995, height: 1280 },
  },
  {
    slug: "g-anitha",
    name: "G Anitha",
    designation: "Professor",
    deptCode: "IS",
    deptName: "Information Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/g-anitha.jpg", alt: "G Anitha, Professor", width: 104, height: 131 },
  },
  {
    slug: "k-c-devendrappa",
    name: "K C Devendrappa",
    designation: "Professor",
    deptCode: "ME",
    deptName: "Mechanical Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/k-c-devendrappa.jpg", alt: "K C Devendrappa, Professor", width: 270, height: 350 },
  },
  {
    slug: "leela-g-h",
    name: "Leela G H",
    designation: "Professor",
    deptCode: "EC",
    deptName: "Electronics & Communication Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/leela-g-h.jpg", alt: "Leela G H, Professor", width: 827, height: 1063 },
  },
  {
    slug: "vijaya-kumar-c-s",
    name: "Vijaya Kumar C S",
    designation: "Professor",
    deptCode: "CV",
    deptName: "Civil Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/vijaya-kumar-c-s.jpg", alt: "Vijaya Kumar C S, Professor", width: 1280, height: 853 },
  },
  {
    slug: "nagaraj-m-s",
    name: "Nagaraj M S",
    designation: "Professor",
    deptCode: "EE",
    deptName: "Electrical & Electronics Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/nagaraj-m-s.jpg", alt: "Nagaraj M S, Professor", width: 270, height: 350 },
  },
  {
    slug: "a-g-shankara-murthy",
    name: "A G Shankara Murthy",
    designation: "Professor",
    deptCode: "ME",
    deptName: "Mechanical Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/a-g-shankara-murthy.jpg", alt: "A G Shankara Murthy, Professor", width: 1991, height: 2560 },
  },
  {
    slug: "s-o-nirmala",
    name: "S O Nirmala",
    designation: "Professor",
    deptCode: "EC",
    deptName: "Electronics & Communication Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/s-o-nirmala.jpg", alt: "S O Nirmala, Professor", width: 995, height: 1280 },
  },
  {
    slug: "sreenivasa-b-r",
    name: "Sreenivasa B R",
    designation: "Professor",
    deptCode: "CG",
    deptName: "Computer Science & Design",
    teaching: true,
    photo: { url: "sites/biet/faculty/sreenivasa-b-r.jpg", alt: "Sreenivasa B R, Professor", width: 390, height: 510 },
  },
  {
    slug: "shadaksharaiah-c",
    name: "Shadaksharaiah C",
    designation: "Assistant Professor",
    deptCode: "CG",
    deptName: "Computer Science & Design",
    teaching: true,
    photo: { url: "sites/biet/faculty/shadaksharaiah-c.jpg", alt: "Shadaksharaiah C, Assistant Professor", width: 1200, height: 1800 },
  },
  {
    slug: "m-n-shivakumar",
    name: "M N Shivakumar",
    designation: "Professor",
    deptCode: "CV",
    deptName: "Civil Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/m-n-shivakumar.jpg", alt: "M N Shivakumar, Professor", width: 1991, height: 2560 },
  },
  {
    slug: "basavanagowda-s-n",
    name: "Basavanagowda S N",
    designation: "Associate Professor",
    deptCode: "CV",
    deptName: "Civil Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/basavanagowda-s-n.jpg", alt: "Basavanagowda S N, Associate Professor", width: 780, height: 1020 },
  },
  {
    slug: "vinutha-h-p",
    name: "Vinutha H P",
    // Matches departments.ts's CSBS/CB head record ("Professor & Head"),
    // which this row previously didn't (2026-08-10) — also now IS's head,
    // dual-appointed, though this single staff.ts row can only carry one
    // department; see departments.ts for the IS side of that.
    designation: "Professor & Head",
    deptCode: "CB",
    deptName: "Computer Science & Business Systems",
    teaching: true,
    // Higher-resolution portrait, same one now used on both her department
    // head records (2026-08-10) — the old faculty/vinutha-h-p.jpg was 587x587.
    photo: { url: "sites/biet/dept-hod/vinutha-h-p.jpg", alt: "Vinutha H P, Professor & Head", width: 1991, height: 2560 },
  },
  {
    slug: "jagadeesh-m-r",
    name: "Jagadeesh M R",
    designation: "Associate Professor & Head",
    deptCode: "PHY",
    deptName: "Physics",
    teaching: true,
    photo: { url: "sites/biet/faculty/jagadeesh-m-r.jpg", alt: "Jagadeesh M R, Associate Professor & Head", width: 780, height: 1020 },
  },
  {
    slug: "prasannakumar-j-k",
    name: "Prasannakumar J K",
    designation: "Associate Professor & Head",
    deptCode: "CHE",
    deptName: "Chemistry",
    teaching: true,
    photo: { url: "sites/biet/faculty/prasannakumar-j-k.jpg", alt: "Prasannakumar J K, Associate Professor & Head", width: 1181, height: 1519 },
  },
  {
    slug: "shankaragowda-b-b",
    name: "Shankaragowda B B",
    designation: "Associate Professor & Head",
    deptCode: "MCA",
    deptName: "Master of Computer Applications",
    teaching: true,
    photo: { url: "sites/biet/faculty/shankaragowda-b-b.jpg", alt: "Shankaragowda B B, Associate Professor & Head", width: 270, height: 350 },
  },
  {
    slug: "veena-kumara-adi",
    name: "Veena Kumara Adi",
    designation: "Associate Professor & Head",
    deptCode: "BT",
    deptName: "Biotechnology",
    teaching: true,
    photo: { url: "sites/biet/faculty/veena-kumara-adi.jpg", alt: "Veena Kumara Adi, Associate Professor & Head", width: 709, height: 601 },
  },
  {
    slug: "gururaj-t",
    name: "Gururaj T",
    designation: "Associate Professor",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/gururaj-t.jpg", alt: "Gururaj T, Associate Professor", width: 413, height: 531 },
  },
  {
    slug: "rekha-c",
    name: "Rekha C",
    designation: "Associate Professor",
    deptCode: "IS",
    deptName: "Information Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/rekha-c.jpg", alt: "Rekha C, Associate Professor", width: 175, height: 231 },
  },
  {
    slug: "ashwini-m-rao",
    name: "Ashwini M Rao",
    designation: "Associate Professor",
    deptCode: "MAT",
    deptName: "Mathematics",
    teaching: true,
    photo: { url: "sites/biet/faculty/ashwini-m-rao.jpg", alt: "Ashwini M Rao, Associate Professor", width: 132, height: 169 },
  },
  {
    slug: "avinash-k-g",
    name: "Avinash K G",
    designation: "Associate Professor",
    deptCode: "EC",
    deptName: "Electronics & Communication Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/avinash-k-g.jpg", alt: "Avinash K G, Associate Professor", width: 1572, height: 2124 },
  },
  {
    slug: "b-m-prasanna",
    name: "B M Prasanna",
    designation: "Associate Professor",
    deptCode: "CHE",
    deptName: "Chemistry",
    teaching: true,
    photo: { url: "sites/biet/faculty/b-m-prasanna.jpg", alt: "B M Prasanna, Associate Professor", width: 134, height: 136 },
  },
  {
    slug: "banumathi-k-l",
    name: "Banumathi K L",
    designation: "Associate Professor",
    deptCode: "EC",
    deptName: "Electronics & Communication Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/banumathi-k-l.jpg", alt: "Banumathi K L, Associate Professor", width: 1991, height: 2560 },
  },
  {
    slug: "suresh-b",
    name: "Suresh B",
    designation: "Associate Professor",
    deptCode: "CV",
    deptName: "Civil Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/suresh-b.jpg", alt: "Suresh B, Associate Professor", width: 124, height: 160 },
  },
  {
    slug: "bhuvaneshwari-k-v",
    name: "Bhuvaneshwari K V",
    designation: "Associate Professor",
    deptCode: "IS",
    deptName: "Information Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/bhuvaneshwari-k-v.jpg", alt: "Bhuvaneshwari K V, Associate Professor", width: 1991, height: 2560 },
  },
  {
    slug: "chandrappa-k-g",
    name: "Chandrappa K G",
    designation: "Associate Professor",
    deptCode: "SH",
    deptName: "Humanities",
    teaching: true,
    photo: { url: "sites/biet/faculty/chandrappa-k-g.jpg", alt: "Chandrappa K G, Associate Professor", width: 420, height: 540 },
  },
  {
    slug: "g-h-kiran-kumar",
    name: "G H Kiran Kumar",
    designation: "Associate Professor",
    deptCode: "EC",
    deptName: "Electronics & Communication Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/g-h-kiran-kumar.jpg", alt: "G H Kiran Kumar, Associate Professor", width: 147, height: 154 },
  },
  {
    slug: "jyothi-g-c",
    name: "Jyothi G C",
    designation: "Associate Professor",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/jyothi-g-c.jpg", alt: "Jyothi G C, Associate Professor", width: 270, height: 350 },
  },
  {
    slug: "m-s-abdul-razak",
    name: "M S Abdul Razak",
    designation: "Associate Professor",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/m-s-abdul-razak.jpg", alt: "M S Abdul Razak, Associate Professor", width: 184, height: 238 },
  },
  {
    slug: "santhosh-k-c",
    name: "Santhosh K C",
    designation: "Associate Professor",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/santhosh-k-c.jpg", alt: "Santhosh K C, Associate Professor", width: 270, height: 350 },
  },
  {
    slug: "k-b-ravindra",
    name: "K B Ravindra",
    designation: "Associate Professor",
    deptCode: "TX",
    deptName: "Textile Technology",
    teaching: true,
    photo: { url: "sites/biet/faculty/k-b-ravindra.jpg", alt: "K B Ravindra, Associate Professor", width: 1034, height: 1280 },
  },
  {
    slug: "k-m-prakash",
    name: "K M Prakash",
    designation: "Associate Professor",
    deptCode: "EC",
    deptName: "Electronics & Communication Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/k-m-prakash.jpg", alt: "K M Prakash, Associate Professor", width: 1991, height: 2560 },
  },
  {
    slug: "naseer-r",
    name: "Naseer R",
    designation: "Associate Professor",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/naseer-r.jpg", alt: "Naseer R, Associate Professor", width: 1080, height: 1388 },
  },
  {
    slug: "mallikarjuna-h-r",
    name: "Mallikarjuna H R",
    designation: "Associate Professor",
    deptCode: "CHE",
    deptName: "Chemistry",
    teaching: true,
    photo: { url: "sites/biet/faculty/mallikarjuna-h-r.jpg", alt: "Mallikarjuna H R, Associate Professor", width: 1181, height: 1519 },
  },
  {
    slug: "n-s-patil",
    name: "N S Patil",
    designation: "Associate Professor",
    deptCode: "IS",
    deptName: "Information Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/n-s-patil.jpg", alt: "N S Patil, Associate Professor", width: 472, height: 591 },
  },
  {
    slug: "naresh-patel-k-m",
    name: "Naresh Patel K M",
    designation: "Associate Professor",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/naresh-patel-k-m.jpg", alt: "Naresh Patel K M, Associate Professor", width: 403, height: 621 },
  },
  {
    slug: "naveen-h-m",
    name: "Naveen H M",
    designation: "Associate Professor",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/naveen-h-m.jpg", alt: "Naveen H M, Associate Professor", width: 270, height: 350 },
  },
  {
    slug: "gangadharappa-s",
    name: "Gangadharappa S",
    designation: "Associate Professor",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/gangadharappa-s.jpg", alt: "Gangadharappa S, Associate Professor", width: 270, height: 350 },
  },
  {
    slug: "naveen-kumar-k-r",
    name: "Naveen Kumar K R",
    designation: "Associate Professor",
    deptCode: "AI",
    deptName: "AI & Machine Learning",
    teaching: true,
    photo: { url: "sites/biet/faculty/naveen-kumar-k-r.jpg", alt: "Naveen Kumar K R, Associate Professor", width: 2096, height: 2000 },
  },
  {
    slug: "ranjana-b-jadekar",
    name: "Ranjana B Jadekar",
    designation: "Assistant Professor",
    deptCode: "CD",
    deptName: "Computer Science & Engineering (Data Science)",
    teaching: true,
    photo: { url: "sites/biet/faculty/ranjana-b-jadekar.jpg", alt: "Ranjana B Jadekar, Assistant Professor", width: 1707, height: 2560 },
  },
  {
    slug: "praveen-kumar-d-g",
    name: "Praveen Kumar  D G",
    designation: "Associate Professor",
    deptCode: "CH",
    deptName: "Chemical Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/praveen-kumar-d-g.jpg", alt: "Praveen Kumar  D G, Associate Professor", width: 210, height: 270 },
  },
  {
    slug: "rajprakash-h-g",
    name: "Rajprakash H G",
    designation: "Associate Professor",
    deptCode: "SH",
    deptName: "Humanities",
    teaching: true,
    photo: { url: "sites/biet/faculty/rajprakash-h-g.jpg", alt: "Rajprakash H G, Associate Professor", width: 180, height: 229 },
  },
  {
    slug: "arunkumar-g-hiremath",
    name: "Arunkumar G Hiremath",
    designation: "Associate Professor",
    deptCode: "IS",
    deptName: "Information Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/arunkumar-g-hiremath.jpg", alt: "Arunkumar G Hiremath, Associate Professor", width: 86, height: 107 },
  },
  {
    slug: "s-m-chandrashekhar",
    name: "S M Chandrashekhar",
    designation: "Associate Professor",
    deptCode: "TX",
    deptName: "Textile Technology",
    teaching: true,
    photo: { url: "sites/biet/faculty/s-m-chandrashekhar.jpg", alt: "S M Chandrashekhar, Associate Professor", width: 668, height: 841 },
  },
  {
    slug: "s-r-basavarajappa",
    name: "S R Basavarajappa",
    designation: "Associate Professor",
    deptCode: "EE",
    deptName: "Electrical & Electronics Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/s-r-basavarajappa.jpg", alt: "S R Basavarajappa, Associate Professor", width: 294, height: 362 },
  },
  {
    slug: "anusha-r",
    name: "Anusha R",
    designation: "Associate Professor",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/anusha-r.jpg", alt: "Anusha R, Associate Professor", width: 1016, height: 994 },
  },
  {
    slug: "santhosh-kumar-m-v",
    name: "Santhosh Kumar M V",
    designation: "Associate Professor",
    deptCode: "PHY",
    deptName: "Physics",
    teaching: true,
    photo: { url: "sites/biet/faculty/santhosh-kumar-m-v.jpg", alt: "Santhosh Kumar M V, Associate Professor", width: 522, height: 690 },
  },
  {
    slug: "sharan-a-s",
    name: "Sharan A S",
    designation: "Associate Professor",
    deptCode: "ME",
    deptName: "Mechanical Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/sharan-a-s.jpg", alt: "Sharan A S, Associate Professor", width: 1991, height: 2560 },
  },
  {
    slug: "shivakumaraswamy-g-m",
    name: "ShivaKumaraSwamy G M",
    designation: "Associate Professor",
    deptCode: "EE",
    deptName: "Electrical & Electronics Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/shivakumaraswamy-g-m.jpg", alt: "ShivaKumaraSwamy G M, Associate Professor", width: 101, height: 130 },
  },
  {
    slug: "raghu-m-e",
    name: "Raghu M E",
    designation: "Assistant Professor",
    deptCode: "CV",
    deptName: "Civil Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/raghu-m-e.jpg", alt: "Raghu M E, Assistant Professor", width: 1200, height: 1800 },
  },
  {
    slug: "usha-g-r",
    name: "Usha G R",
    designation: "Associate Professor",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/usha-g-r.jpg", alt: "Usha G R, Associate Professor", width: 1201, height: 1600 },
  },
  {
    slug: "varsha-m",
    name: "Varsha M",
    designation: "Associate Professor",
    deptCode: "AI",
    deptName: "AI & Machine Learning",
    teaching: true,
    photo: { url: "sites/biet/faculty/varsha-m.jpg", alt: "Varsha M, Associate Professor", width: 1874, height: 2560 },
  },
  {
    slug: "shwetha-g",
    name: "Shwetha G",
    designation: "Assistant Professor",
    deptCode: "CG",
    deptName: "Computer Science & Design",
    teaching: true,
    photo: { url: "sites/biet/faculty/shwetha-g.jpg", alt: "Shwetha G, Assistant Professor", width: 1060, height: 1256 },
  },
  {
    slug: "b-r-raghu",
    name: "B R Raghu",
    designation: "Assistant Professor",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/b-r-raghu.jpg", alt: "B R Raghu, Assistant Professor", width: 254, height: 337 },
  },
  {
    slug: "sheik-imran",
    name: "Sheik Imran",
    designation: "Assistant Professor",
    deptCode: "AI",
    deptName: "AI & Machine Learning",
    teaching: true,
    photo: { url: "sites/biet/faculty/sheik-imran.jpg", alt: "Sheik Imran, Assistant Professor", width: 624, height: 780 },
  },
  {
    slug: "puneeth-b-h",
    name: "Puneeth B H",
    designation: "Assistant Professor",
    deptCode: "AI",
    deptName: "Artificial Intelligence & Machine Learning",
    teaching: true,
    photo: { url: "sites/biet/faculty/puneeth-b-h.jpg", alt: "Puneeth B H, Assistant Professor", width: 1200, height: 1269 },
  },
  {
    slug: "m-vagish",
    name: "M Vagish",
    designation: "Assistant Professor",
    deptCode: "CV",
    deptName: "Civil Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/m-vagish.jpg", alt: "M Vagish, Assistant Professor", width: 396, height: 539 },
  },
  {
    slug: "ali-i-k",
    name: "Ali I K",
    designation: "Assistant Professor",
    deptCode: "EC",
    deptName: "Electronics & Communication Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/ali-i-k.jpg", alt: "Ali I K, Assistant Professor", width: 1591, height: 2449 },
  },
  {
    slug: "amarnath-p-c",
    name: "Amarnath  P C",
    designation: "Assistant Professor",
    deptCode: "CH",
    deptName: "Chemical Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/amarnath-p-c.jpg", alt: "Amarnath  P C, Assistant Professor", width: 252, height: 324 },
  },
  {
    slug: "anil-kumar-k-n",
    name: "Anil Kumar K N",
    designation: "Assistant Professor",
    deptCode: "MAT",
    deptName: "Mathematics",
    teaching: true,
    photo: { url: "sites/biet/faculty/anil-kumar-k-n.jpg", alt: "Anil Kumar K N, Assistant Professor", width: 132, height: 169 },
  },
  {
    slug: "radhika-patil",
    name: "Radhika Patil",
    designation: "Assistant Professor",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/radhika-patil.jpg", alt: "Radhika Patil, Assistant Professor", width: 527, height: 571 },
  },
  {
    slug: "a-n-jagadesh",
    name: "A N Jagadesh",
    designation: "Assistant Professor",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/a-n-jagadesh.jpg", alt: "A N Jagadesh, Assistant Professor", width: 1960, height: 2560 },
  },
  {
    slug: "anu-c-s",
    name: "Anu C S",
    designation: "Assistant Professor",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/anu-c-s.jpg", alt: "Anu C S, Assistant Professor", width: 1921, height: 2521 },
  },
  {
    slug: "anusha-r-s",
    name: "Anusha R S",
    designation: "Assistant Professor",
    deptCode: "MAT",
    deptName: "Mathematics",
    teaching: true,
    photo: { url: "sites/biet/faculty/anusha-r-s.jpg", alt: "Anusha R S, Assistant Professor", width: 838, height: 1154 },
  },
  {
    slug: "waseem-khan",
    name: "Waseem Khan",
    designation: "Assistant Professor",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/waseem-khan.jpg", alt: "Waseem Khan, Assistant Professor", width: 1991, height: 2560 },
  },
  {
    slug: "gangamma-g-hediyalad",
    name: "Gangamma G Hediyalad",
    designation: "Assistant Professor",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/gangamma-g-hediyalad.jpg", alt: "Gangamma G Hediyalad, Assistant Professor", width: 1921, height: 2537 },
  },
  {
    slug: "praveen-kumar-g-b",
    name: "Praveen Kumar G B",
    designation: "Assistant Professor",
    deptCode: "CV",
    deptName: "Civil Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/praveen-kumar-g-b.jpg", alt: "Praveen Kumar G B, Assistant Professor", width: 1080, height: 1245 },
  },
  {
    slug: "manjunatha-h-m",
    name: "Manjunatha H M",
    designation: "Assistant Professor",
    deptCode: "EE",
    deptName: "Electrical & Electronics Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/manjunatha-h-m.jpg", alt: "Manjunatha H M, Assistant Professor", width: 750, height: 694 },
  },
  {
    slug: "d-e-umesh",
    name: "D E Umesh",
    designation: "Assistant Professor",
    deptCode: "ME",
    deptName: "Mechanical Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/d-e-umesh.jpg", alt: "D E  Umesh, Assistant Professor", width: 1991, height: 2560 },
  },
  {
    slug: "karibasavaraju-t-s",
    name: "Karibasavaraju T S",
    designation: "Assistant Professor",
    deptCode: "EE",
    deptName: "Electrical & Electronics Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/karibasavaraju-t-s.jpg", alt: "Karibasavaraju T S, Assistant Professor", width: 119, height: 153 },
  },
  {
    slug: "preethi-b",
    name: "Preethi B",
    designation: "Assistant Professor",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/preethi-b.jpg", alt: "Preethi B, Assistant Professor", width: 1991, height: 2560 },
  },
  {
    slug: "basavaraj-hugar",
    name: "Basavaraj Hugar",
    designation: "Assistant Professor",
    deptCode: "SH",
    deptName: "Humanities",
    teaching: true,
    photo: { url: "sites/biet/faculty/basavaraj-hugar.jpg", alt: "Basavaraj Hugar, Assistant Professor", width: 413, height: 531 },
  },
  {
    slug: "vijayalakshmi-s-hallikeri",
    name: "Vijayalakshmi S Hallikeri",
    designation: "Assistant Professor",
    deptCode: "AI",
    deptName: "AI & Machine Learning",
    teaching: true,
    photo: { url: "sites/biet/faculty/vijayalakshmi-s-hallikeri.jpg", alt: "Vijayalakshmi S Hallikeri, Assistant Professor", width: 1991, height: 2560 },
  },
  {
    slug: "bhagya-shanthakumar",
    name: "Bhagya Shanthakumar",
    designation: "Assistant Professor",
    deptCode: "EC",
    deptName: "Electronics & Communication Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/bhagya-shanthakumar.jpg", alt: "Bhagya Shanthakumar, Assistant Professor", width: 1991, height: 2560 },
  },
  {
    slug: "vinay-m-t",
    name: "Vinay M T",
    designation: "Assistant Professor",
    deptCode: "CD",
    deptName: "Computer Science & Engineering (Data Science)",
    teaching: true,
    photo: { url: "sites/biet/faculty/vinay-m-t.jpg", alt: "Vinay M T, Assistant Professor", width: 1991, height: 2560 },
  },
  {
    slug: "bhumika-p-s",
    name: "Bhumika P S",
    designation: "Assistant Professor",
    deptCode: "CHE",
    deptName: "Chemistry",
    teaching: true,
    photo: { url: "sites/biet/faculty/bhumika-p-s.jpg", alt: "Bhumika P S, Assistant Professor", width: 1008, height: 792 },
  },
  {
    slug: "vasudevanayaka-k-b-l",
    name: "Vasudevanayaka K B L",
    designation: "Assistant Professor",
    deptCode: "BT",
    deptName: "Biotechnology",
    teaching: true,
    photo: { url: "sites/biet/faculty/vasudevanayaka-k-b-l.jpg", alt: "Vasudevanayaka K B L, Assistant Professor", width: 262, height: 303 },
  },
  {
    slug: "bindu-b-v",
    name: "Bindu B V",
    designation: "Assistant Professor",
    deptCode: "MCA",
    deptName: "Master of Computer Applications",
    teaching: true,
    photo: { url: "sites/biet/faculty/bindu-b-v.jpg", alt: "Bindu B V, Assistant Professor", width: 743, height: 954 },
  },
  {
    slug: "chaitra-a-p",
    name: "Chaitra A P",
    designation: "Assistant Professor",
    deptCode: "MAT",
    deptName: "Mathematics",
    teaching: true,
    photo: { url: "sites/biet/faculty/chaitra-a-p.jpg", alt: "Chaitra A P, Assistant Professor", width: 636, height: 864 },
  },
  {
    slug: "siddesh-a-s",
    name: "Siddesh A S",
    designation: "Assistant Professor",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/siddesh-a-s.jpg", alt: "Siddesh A S, Assistant Professor", width: 727, height: 930 },
  },
  {
    slug: "puneeth-s-p",
    name: "Puneeth S P",
    designation: "Assistant Professor",
    deptCode: "IS",
    deptName: "Information Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/puneeth-s-p.jpg", alt: "Puneeth S P, Assistant Professor", width: 2100, height: 1883 },
  },
  {
    slug: "hemashree-h-c",
    name: "Hemashree H C",
    designation: "Assistant Professor",
    deptCode: "IS",
    deptName: "Information Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/hemashree-h-c.jpg", alt: "Hemashree H C, Assistant Professor", width: 1280, height: 1499 },
  },
  {
    slug: "chandrashekhar-m-v",
    name: "Chandrashekhar M V",
    designation: "Assistant Professor",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/chandrashekhar-m-v.jpg", alt: "Chandrashekhar M V, Assistant Professor", width: 413, height: 443 },
  },
  {
    slug: "arpitha-d-j",
    name: "Arpitha D J",
    designation: "Assistant Professor",
    deptCode: "CV",
    deptName: "Civil Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/arpitha-d-j.jpg", alt: "Arpitha D J, Assistant Professor", width: 378, height: 501 },
  },
  {
    slug: "prashanth-s-j",
    name: "Prashanth S J",
    designation: "Assistant Professor",
    deptCode: "ME",
    deptName: "Mechanical Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/prashanth-s-j.jpg", alt: "Prashanth S J, Assistant Professor", width: 1991, height: 2560 },
  },
  {
    slug: "bhagya-s-g",
    name: "Bhagya S G",
    designation: "Assistant Professor",
    deptCode: "AI",
    deptName: "AI & Machine Learning",
    teaching: true,
    photo: { url: "sites/biet/faculty/bhagya-s-g.jpg", alt: "Bhagya S G, Assistant Professor", width: 150, height: 144 },
  },
  {
    slug: "shivamurthy-d",
    name: "Shivamurthy D",
    designation: "Assistant Professor",
    deptCode: "CB",
    deptName: "Computer Science & Business Systems",
    teaching: true,
    photo: { url: "sites/biet/faculty/shivamurthy-d.jpg", alt: "Shivamurthy D, Assistant Professor", width: 1872, height: 2324 },
  },
  {
    slug: "rahima-b",
    name: "Rahima B",
    designation: "Assistant Professor",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/rahima-b.jpg", alt: "Rahima B, Assistant Professor", width: 694, height: 726 },
  },
  {
    slug: "vishwanath-v-k",
    name: "Vishwanath V K",
    designation: "Assistant Professor",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/vishwanath-v-k.jpg", alt: "Vishwanath V K, Assistant Professor", width: 640, height: 816 },
  },
  {
    slug: "gowramma-b-h",
    name: "Gowramma B H",
    designation: "Assistant Professor",
    deptCode: "CD",
    deptName: "Computer Science & Engineering (Data Science)",
    teaching: true,
    photo: { url: "sites/biet/faculty/gowramma-b-h.jpg", alt: "Gowramma B H, Assistant Professor", width: 343, height: 358 },
  },
  {
    slug: "naveen-g",
    name: "Naveen G",
    designation: "Assistant Professor",
    deptCode: "AI",
    deptName: "AI & Machine Learning",
    teaching: true,
    photo: { url: "sites/biet/faculty/naveen-g.jpg", alt: "Naveen G, Assistant Professor", width: 596, height: 860 },
  },
  {
    slug: "parveen-banu-n",
    name: "Parveen Banu N",
    designation: "Assistant Professor",
    deptCode: "IS",
    deptName: "Information Science & Engineering",
    teaching: true,
    // Absent from the HR spreadsheet's 394 photographed rows (see the file
    // note above), but found in the IS gallery sweep, filed under her own
    // name (t-is-09-smt-parveen-banu-n.jpg) and excluded from the public
    // gallery there as a portrait — re-ingested here as her faculty photo.
    photo: { url: "sites/biet/faculty/parveen-banu-n.jpg", alt: "Parveen Banu N, Assistant Professor", width: 1991, height: 2560 },
  },
  {
    slug: "gururaj-l-kulkarni",
    name: "Gururaj L Kulkarni",
    designation: "Assistant Professor",
    deptCode: "AI",
    deptName: "AI & Machine Learning",
    teaching: true,
    photo: { url: "sites/biet/faculty/gururaj-l-kulkarni.jpg", alt: "Gururaj L Kulkarni, Assistant Professor", width: 1279, height: 1482 },
  },
  {
    slug: "jyothika-k-r",
    name: "Jyothika K R",
    designation: "Assistant Professor",
    deptCode: "MCA",
    deptName: "Master of Computer Applications",
    teaching: true,
    photo: { url: "sites/biet/faculty/jyothika-k-r.jpg", alt: "Jyothika K R, Assistant Professor", width: 1505, height: 1938 },
  },
  {
    slug: "vijaykumar-t-n",
    name: "Vijaykumar T N",
    designation: "Assistant Professor",
    deptCode: "ME",
    deptName: "Mechanical Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/vijaykumar-t-n.jpg", alt: "Vijaykumar T N, Assistant Professor", width: 300, height: 400 },
  },
  {
    slug: "k-g-suma",
    name: "K G Suma",
    designation: "Assistant Professor",
    deptCode: "EC",
    deptName: "Electronics & Communication Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/k-g-suma.jpg", alt: "K G Suma, Assistant Professor", width: 1991, height: 2560 },
  },
  {
    slug: "rekha-b-h",
    name: "Rekha B H",
    designation: "Assistant Professor",
    deptCode: "IS",
    deptName: "Information Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/rekha-b-h.jpg", alt: "Rekha B H, Assistant Professor", width: 1024, height: 1316 },
  },
  {
    slug: "shilpa-s-k",
    name: "Shilpa S K",
    designation: "Assistant Professor",
    deptCode: "EE",
    deptName: "Electrical & Electronics Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/shilpa-s-k.jpg", alt: "Shilpa S K, Assistant Professor", width: 270, height: 350 },
  },
  {
    slug: "kaveri-c",
    name: "Kaveri C",
    designation: "Assistant Professor",
    deptCode: "CG",
    deptName: "Computer Science & Design",
    teaching: true,
    photo: { url: "sites/biet/faculty/kaveri-c.jpg", alt: "Kaveri C, Assistant Professor", width: 974, height: 1194 },
  },
  {
    slug: "kavitha-p-g",
    name: "Kavitha P G",
    designation: "Assistant Professor",
    deptCode: "PHY",
    deptName: "Physics",
    teaching: true,
    photo: { url: "sites/biet/faculty/kavitha-p-g.jpg", alt: "Kavitha P G, Assistant Professor", width: 1575, height: 2025 },
  },
  {
    slug: "kavya-l",
    name: "Kavya L",
    designation: "Assistant Professor",
    deptCode: "TX",
    deptName: "Textile Technology",
    teaching: true,
    photo: { url: "sites/biet/faculty/kavya-l.jpg", alt: "Kavya L, Assistant Professor", width: 206, height: 242 },
  },
  {
    slug: "madhu-n-hiremath",
    name: "Madhu N Hiremath",
    designation: "Assistant Professor",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/madhu-n-hiremath.jpg", alt: "Madhu N Hiremath, Assistant Professor", width: 989, height: 1186 },
  },
  {
    slug: "swetha-h-u",
    name: "Swetha H U",
    designation: "Assistant Professor",
    deptCode: "AI",
    deptName: "AI & Machine Learning",
    teaching: true,
    photo: { url: "sites/biet/faculty/swetha-h-u.jpg", alt: "Swetha H U, Assistant Professor", width: 1173, height: 1280 },
  },
  {
    slug: "kotru-swamy-s-m",
    name: "Kotru Swamy S M",
    designation: "Assistant Professor",
    deptCode: "MCA",
    deptName: "Master of Computer Applications",
    teaching: true,
    photo: { url: "sites/biet/faculty/kotru-swamy-s-m.jpg", alt: "Kotru Swamy S M, Assistant Professor", width: 1600, height: 985 },
  },
  {
    slug: "bharath-h-c",
    name: "Bharath H C",
    designation: "Assistant Professor",
    deptCode: "CD",
    deptName: "Computer Science & Engineering (Data Science)",
    teaching: true,
    photo: { url: "sites/biet/faculty/bharath-h-c.jpg", alt: "Bharath H C, Assistant Professor", width: 268, height: 361 },
  },
  {
    slug: "chetan-gonni-s",
    name: "Chetan Gonni S",
    designation: "Assistant Professor",
    deptCode: "CV",
    deptName: "Civil Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/chetan-gonni-s.jpg", alt: "Chetan Gonni S, Assistant Professor", width: 260, height: 340 },
  },
  {
    slug: "priyanka-d-v",
    name: "Priyanka D V",
    designation: "Assistant Professor",
    deptCode: "CB",
    deptName: "Computer Science & Business Systems",
    teaching: true,
    photo: { url: "sites/biet/faculty/priyanka-d-v.jpg", alt: "Priyanka D V, Assistant Professor", width: 1280, height: 1217 },
  },
  {
    slug: "shankar-sarji-p",
    name: "Shankar Sarji P",
    designation: "Assistant Professor",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/shankar-sarji-p.jpg", alt: "Shankar Sarji P, Assistant Professor", width: 1542, height: 2314 },
  },
  {
    slug: "madhuri-deekshith-s",
    name: "Madhuri Deekshith S",
    designation: "Assistant Professor",
    deptCode: "CG",
    deptName: "Computer Science & Design",
    teaching: true,
    photo: { url: "sites/biet/faculty/madhuri-deekshith-s.jpg", alt: "Madhuri Deekshith S, Assistant Professor", width: 1113, height: 1280 },
  },
  {
    slug: "santhosh-t",
    name: "Santhosh T",
    designation: "Assistant Professor",
    deptCode: "IS",
    deptName: "Information Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/santhosh-t.jpg", alt: "Santhosh T, Assistant Professor", width: 800, height: 1100 },
  },
  {
    slug: "mahantesh-havagar",
    name: "Mahantesh Havagar",
    designation: "Assistant Professor",
    deptCode: "EC",
    deptName: "Electronics & Communication Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/mahantesh-havagar.jpg", alt: "Mahantesh Havagar, Assistant Professor", width: 360, height: 450 },
  },
  {
    slug: "santosha-p",
    name: "Santosha P",
    designation: "Assistant Professor",
    deptCode: "AI",
    deptName: "AI & Machine Learning",
    teaching: true,
    photo: { url: "sites/biet/faculty/santosha-p.jpg", alt: "Santosha P, Assistant Professor", width: 413, height: 531 },
  },
  {
    slug: "shruthi-s-r",
    name: "Shruthi S R",
    designation: "Assistant Professor",
    deptCode: "BT",
    deptName: "Biotechnology",
    teaching: true,
    photo: { url: "sites/biet/faculty/shruthi-s-r.jpg", alt: "Shruthi S R, Assistant Professor", width: 630, height: 810 },
  },
  {
    slug: "manjunath-k-s",
    name: "Manjunath K S",
    designation: "Assistant Professor",
    deptCode: "CHE",
    deptName: "Chemistry",
    teaching: true,
    photo: { url: "sites/biet/faculty/manjunath-k-s.jpg", alt: "Manjunath K S, Assistant Professor", width: 797, height: 1024 },
  },
  {
    slug: "ashoka-e",
    name: "Ashoka E",
    designation: "Assistant Professor",
    deptCode: "ME",
    deptName: "Mechanical Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/ashoka-e.jpg", alt: "Ashoka E, Assistant Professor", width: 256, height: 256 },
  },
  {
    slug: "soumya-anand",
    name: "Soumya Anand",
    designation: "Assistant Professor",
    deptCode: "EE",
    deptName: "Electrical & Electronics Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/soumya-anand.jpg", alt: "Soumya Anand, Assistant Professor", width: 139, height: 154 },
  },
  {
    slug: "madhuri-martis",
    name: "Madhuri Martis",
    designation: "Assistant Professor",
    deptCode: "IS",
    deptName: "Information Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/madhuri-martis.jpg", alt: "Madhuri Martis, Assistant Professor", width: 780, height: 1020 },
  },
  {
    slug: "rachana-g-s",
    name: "Rachana G S",
    designation: "Assistant Professor",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/rachana-g-s.jpg", alt: "Rachana G S, Assistant Professor", width: 560, height: 552 },
  },
  {
    slug: "mohan-t-r",
    name: "Mohan T R",
    designation: "Assistant Professor",
    deptCode: "ME",
    deptName: "Mechanical Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/mohan-t-r.jpg", alt: "Mohan T R, Assistant Professor", width: 640, height: 640 },
  },
  {
    slug: "nagarathnamma-k-g",
    name: "Nagarathnamma K G",
    designation: "Assistant Professor",
    deptCode: "MAT",
    deptName: "Mathematics",
    teaching: true,
    photo: { url: "sites/biet/faculty/nagarathnamma-k-g.jpg", alt: "Nagarathnamma K G, Assistant Professor", width: 1836, height: 2127 },
  },
  {
    slug: "kamala-r",
    name: "Kamala R",
    designation: "Assistant Professor",
    deptCode: "IS",
    deptName: "Information Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/kamala-r.jpg", alt: "Kamala R, Assistant Professor", width: 124, height: 147 },
  },
  {
    slug: "nandeesh-r-s",
    name: "Nandeesh R S",
    designation: "Assistant Professor",
    deptCode: "TX",
    deptName: "Textile Technology",
    teaching: true,
    photo: { url: "sites/biet/faculty/nandeesh-r-s.jpg", alt: "Nandeesh R S, Assistant Professor", width: 1012, height: 1228 },
  },
  {
    slug: "kotramma-t-s",
    name: "Kotramma T S",
    designation: "Assistant Professor",
    deptCode: "AI",
    deptName: "AI & Machine Learning",
    teaching: true,
    photo: { url: "sites/biet/faculty/kotramma-t-s.jpg", alt: "Kotramma T S, Assistant Professor", width: 413, height: 531 },
  },
  {
    slug: "navya-k-g",
    name: "Navya K G",
    designation: "Assistant Professor",
    deptCode: "CG",
    deptName: "Computer Science & Design",
    teaching: true,
    photo: { url: "sites/biet/faculty/navya-k-g.jpg", alt: "Navya K G, Assistant Professor", width: 340, height: 340 },
  },
  {
    slug: "satish-g-n",
    name: "Satish G N",
    designation: "Assistant Professor",
    deptCode: "CV",
    deptName: "Civil Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/satish-g-n.jpg", alt: "Satish G N, Assistant Professor", width: 413, height: 531 },
  },
  {
    slug: "arjun-h",
    name: "Arjun H",
    designation: "Assistant Professor",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/arjun-h.jpg", alt: "Arjun H, Assistant Professor", width: 413, height: 531 },
  },
  {
    slug: "pallavi-g-h",
    name: "Pallavi G H",
    designation: "Assistant Professor",
    deptCode: "MAT",
    deptName: "Mathematics",
    teaching: true,
    photo: { url: "sites/biet/faculty/pallavi-g-h.jpg", alt: "Pallavi G H, Assistant Professor", width: 521, height: 589 },
  },
  {
    slug: "pallavi-g-s",
    name: "Pallavi G S",
    designation: "Assistant Professor",
    deptCode: "MAT",
    deptName: "Mathematics",
    teaching: true,
    photo: { url: "sites/biet/faculty/pallavi-g-s.jpg", alt: "Pallavi G S, Assistant Professor", width: 1335, height: 1828 },
  },
  {
    slug: "manjushree-k-r",
    name: "Manjushree K R",
    designation: "Assistant Professor",
    deptCode: "IS",
    deptName: "Information Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/manjushree-k-r.jpg", alt: "Manjushree K R, Assistant Professor", width: 1668, height: 2164 },
  },
  {
    slug: "roopa-d-e",
    name: "Roopa D E",
    designation: "Assistant Professor",
    deptCode: "IS",
    deptName: "Information Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/roopa-d-e.jpg", alt: "Roopa D E, Assistant Professor", width: 780, height: 1020 },
  },
  {
    slug: "pavan-kumar-d",
    name: "Pavan Kumar D",
    designation: "Assistant Professor",
    deptCode: "EC",
    deptName: "Electronics & Communication Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/pavan-kumar-d.jpg", alt: "Pavan Kumar D, Assistant Professor", width: 640, height: 640 },
  },
  {
    slug: "pavithra-k-s",
    name: "Pavithra K S",
    designation: "Assistant Professor",
    deptCode: "CHE",
    deptName: "Chemistry",
    teaching: true,
    photo: { url: "sites/biet/faculty/pavithra-k-s.jpg", alt: "Pavithra K S, Assistant Professor", width: 1004, height: 981 },
  },
  {
    slug: "pooja-k-n-em1386",
    name: "Pooja K N",
    designation: "Assistant Professor",
    deptCode: "MCA",
    deptName: "Master of Computer Applications",
    teaching: true,
    photo: { url: "sites/biet/faculty/pooja-k-n-em1386.jpg", alt: "Pooja K N, Assistant Professor", width: 1920, height: 2560 },
  },
  {
    slug: "pooja-k-n-em1459",
    name: "Pooja K N",
    designation: "Assistant Professor",
    deptCode: "MAT",
    deptName: "Mathematics",
    teaching: true,
    photo: { url: "sites/biet/faculty/pooja-k-n-em1459.jpg", alt: "Pooja K N, Assistant Professor", width: 1086, height: 1449 },
  },
  {
    slug: "poornima-g-n",
    name: "Poornima G N",
    designation: "Assistant Professor",
    deptCode: "EC",
    deptName: "Electronics & Communication Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/poornima-g-n.jpg", alt: "Poornima G N, Assistant Professor", width: 210, height: 283 },
  },
  {
    slug: "k-b-hemanna",
    name: "K B Hemanna",
    designation: "Assistant Professor",
    deptCode: "ME",
    deptName: "Mechanical Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/k-b-hemanna.jpg", alt: "K B Hemanna, Assistant Professor", width: 1991, height: 2560 },
  },
  {
    slug: "pradeepkumar-j",
    name: "Pradeepkumar J",
    designation: "Assistant Professor",
    deptCode: "MAT",
    deptName: "Mathematics",
    teaching: true,
    photo: { url: "sites/biet/faculty/pradeepkumar-j.jpg", alt: "Pradeepkumar J, Assistant Professor", width: 593, height: 570 },
  },
  {
    slug: "prashanth-kumar-h-k",
    name: "Prashanth Kumar H K",
    designation: "Assistant Professor",
    deptCode: "EE",
    deptName: "Electrical & Electronics Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/prashanth-kumar-h-k.jpg", alt: "Prashanth Kumar H K, Assistant Professor", width: 1991, height: 2560 },
  },
  {
    slug: "vinayaka-patil-a-b",
    name: "Vinayaka Patil A B",
    designation: "Assistant Professor",
    deptCode: "ME",
    deptName: "Mechanical Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/vinayaka-patil-a-b.jpg", alt: "Vinayaka Patil A B, Assistant Professor", width: 1991, height: 2560 },
  },
  {
    slug: "sumana-y-b",
    name: "Sumana Y B",
    designation: "Assistant Professor",
    deptCode: "CV",
    deptName: "Civil Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/sumana-y-b.jpg", alt: "Sumana Y B, Assistant Professor", width: 908, height: 990 },
  },
  {
    slug: "anusha-n",
    name: "Anusha N",
    designation: "Assistant Professor",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/anusha-n.jpg", alt: "Anusha N, Assistant Professor", width: 586, height: 603 },
  },
  {
    slug: "deepa-k-b",
    name: "Deepa K B",
    designation: "Assistant Professor",
    deptCode: "AI",
    deptName: "AI & Machine Learning",
    teaching: true,
    photo: { url: "sites/biet/faculty/deepa-k-b.jpg", alt: "Deepa K B, Assistant Professor", width: 437, height: 557 },
  },
  {
    slug: "priyanka-r-navale",
    name: "Priyanka R Navale",
    designation: "Assistant Professor",
    deptCode: "CB",
    deptName: "Computer Science & Business Systems",
    teaching: true,
    photo: { url: "sites/biet/faculty/priyanka-r-navale.jpg", alt: "Priyanka R Navale, Assistant Professor", width: 402, height: 520 },
  },
  {
    slug: "divya-patil",
    name: "Divya Patil",
    designation: "Assistant Professor",
    deptCode: "CB",
    deptName: "Computer Science & Business Systems",
    teaching: true,
    photo: { url: "sites/biet/faculty/divya-patil.jpg", alt: "Divya Patil, Assistant Professor", width: 1186, height: 1300 },
  },
  {
    slug: "akash-c-arakere",
    name: "Akash C Arakere",
    designation: "Assistant Professor",
    deptCode: "CV",
    deptName: "Civil Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/akash-c-arakere.jpg", alt: "Akash C Arakere, Assistant Professor", width: 375, height: 412 },
  },
  {
    slug: "priya-v",
    name: "Priya V",
    designation: "Assistant Professor",
    deptCode: "CB",
    deptName: "Computer Science & Business Systems",
    teaching: true,
    photo: { url: "sites/biet/faculty/priya-v.jpg", alt: "Priya V, Assistant Professor", width: 1097, height: 1280 },
  },
  {
    slug: "madhu-b-g",
    name: "Madhu B G",
    designation: "Assistant Professor",
    deptCode: "CB",
    deptName: "Computer Science & Business Systems",
    teaching: true,
    photo: { url: "sites/biet/faculty/madhu-b-g.jpg", alt: "Madhu B G, Assistant Professor", width: 1920, height: 2560 },
  },
  {
    slug: "aishwarya-t",
    name: "Aishwarya T",
    designation: "Assistant Professor",
    deptCode: "AI",
    deptName: "AI & Machine Learning",
    teaching: true,
    photo: { url: "sites/biet/faculty/aishwarya-t.jpg", alt: "Aishwarya T, Assistant Professor", width: 1103, height: 1426 },
  },
  {
    slug: "sushma-c",
    name: "Sushma C",
    designation: "Assistant Professor",
    deptCode: "IS",
    deptName: "Information Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/sushma-c.jpg", alt: "Sushma C, Assistant Professor", width: 780, height: 1020 },
  },
  {
    slug: "archana-t-chawhan",
    name: "Archana T Chawhan",
    designation: "Assistant Professor",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/archana-t-chawhan.jpg", alt: "Archana T Chawhan, Assistant Professor", width: 1439, height: 1394 },
  },
  {
    slug: "nomitha-chawla",
    name: "Nomitha Chawla",
    designation: "Assistant Professor",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/nomitha-chawla.jpg", alt: "Nomitha Chawla, Assistant Professor", width: 1704, height: 2560 },
  },
  {
    slug: "radhika-priya-y-r",
    name: "Radhika Priya Y R",
    designation: "Assistant Professor",
    deptCode: "EC",
    deptName: "Electronics & Communication Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/radhika-priya-y-r.jpg", alt: "Radhika Priya Y R, Assistant Professor", width: 1991, height: 2560 },
  },
  {
    slug: "sharathraj-r-m",
    name: "Sharathraj R M",
    designation: "Assistant Professor",
    deptCode: "CV",
    deptName: "Civil Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/sharathraj-r-m.jpg", alt: "Sharathraj R M, Assistant Professor", width: 826, height: 1062 },
  },
  {
    slug: "ranjitha-h-s",
    name: "Ranjitha H S",
    designation: "Assistant Professor",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/ranjitha-h-s.jpg", alt: "Ranjitha H S, Assistant Professor", width: 952, height: 1280 },
  },
  {
    slug: "usha-c",
    name: "Usha C",
    designation: "Assistant Professor",
    deptCode: "CD",
    deptName: "Computer Science & Engineering (Data Science)",
    teaching: true,
    photo: { url: "sites/biet/faculty/usha-c.jpg", alt: "Usha C, Assistant Professor", width: 1200, height: 1599 },
  },
  {
    slug: "chaitra-k-c",
    name: "Chaitra K C",
    designation: "Assistant Professor",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/chaitra-k-c.jpg", alt: "Chaitra K C, Assistant Professor", width: 413, height: 531 },
  },
  {
    slug: "pradeep-n-r",
    name: "Pradeep N R",
    designation: "Assistant Professor",
    deptCode: "ME",
    deptName: "Mechanical Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/pradeep-n-r.jpg", alt: "Pradeep N R, Assistant Professor", width: 240, height: 320 },
  },
  {
    slug: "nagashri-j-a",
    name: "Nagashri J A",
    designation: "Assistant Professor",
    deptCode: "IS",
    deptName: "Information Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/nagashri-j-a.jpg", alt: "Nagashri J A, Assistant Professor", width: 780, height: 1020 },
  },
  {
    slug: "rekha-m-b",
    name: "Rekha M B",
    designation: "Assistant Professor",
    deptCode: "MAT",
    deptName: "Mathematics",
    teaching: true,
    photo: { url: "sites/biet/faculty/rekha-m-b.jpg", alt: "Rekha M B, Assistant Professor", width: 864, height: 1184 },
  },
  {
    slug: "renuka-b-n",
    name: "Renuka B N",
    designation: "Assistant Professor",
    deptCode: "MCA",
    deptName: "Master of Computer Applications",
    teaching: true,
    photo: { url: "sites/biet/faculty/renuka-b-n.jpg", alt: "Renuka B N, Assistant Professor", width: 1413, height: 1309 },
  },
  {
    slug: "reshma-m-totager",
    name: "Reshma M Totager",
    designation: "Assistant Professor",
    deptCode: "MAT",
    deptName: "Mathematics",
    teaching: true,
    photo: { url: "sites/biet/faculty/reshma-m-totager.jpg", alt: "Reshma M Totager, Assistant Professor", width: 207, height: 244 },
  },
  {
    slug: "vinutha-d",
    name: "Vinutha D",
    designation: "Assistant Professor",
    deptCode: "IS",
    deptName: "Information Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/vinutha-d.jpg", alt: "Vinutha D, Assistant Professor", width: 1919, height: 2560 },
  },
  {
    slug: "s-s-nandikol",
    name: "S S Nandikol",
    designation: "Assistant Professor",
    deptCode: "TX",
    deptName: "Textile Technology",
    teaching: true,
    photo: { url: "sites/biet/faculty/s-s-nandikol.jpg", alt: "S S Nandikol, Assistant Professor", width: 1048, height: 1219 },
  },
  {
    slug: "chaitra-k-j",
    name: "Chaitra K J",
    designation: "Assistant Professor",
    deptCode: "IS",
    deptName: "Information Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/chaitra-k-j.jpg", alt: "Chaitra K J, Assistant Professor", width: 102, height: 135 },
  },
  {
    slug: "mohamed-muthahir-r",
    name: "Mohamed Muthahir R",
    designation: "Assistant Professor",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/mohamed-muthahir-r.jpg", alt: "Mohamed Muthahir R, Assistant Professor", width: 780, height: 1020 },
  },
  {
    slug: "santhosh-h-b",
    name: "Santhosh H B",
    designation: "Assistant Professor",
    deptCode: "MAT",
    deptName: "Mathematics",
    teaching: true,
    photo: { url: "sites/biet/faculty/santhosh-h-b.jpg", alt: "Santhosh H B, Assistant Professor", width: 450, height: 488 },
  },
  {
    slug: "chaitra-k-g",
    name: "Chaitra K G",
    designation: "Assistant Professor",
    deptCode: "IS",
    deptName: "Information Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/chaitra-k-g.jpg", alt: "Chaitra K G, Assistant Professor", width: 848, height: 1264 },
  },
  {
    slug: "shinila-h-s",
    name: "Shinila H S",
    designation: "Assistant Professor",
    deptCode: "AI",
    deptName: "AI & Machine Learning",
    teaching: true,
    photo: { url: "sites/biet/faculty/shinila-h-s.jpg", alt: "Shinila H S, Assistant Professor", width: 1086, height: 1448 },
  },
  {
    slug: "shahabaz-hakeem",
    name: "Shahabaz Hakeem",
    designation: "Assistant Professor",
    deptCode: "CV",
    deptName: "Civil Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/shahabaz-hakeem.jpg", alt: "Shahabaz Hakeem, Assistant Professor", width: 576, height: 668 },
  },
  {
    slug: "savan-r-g-basavaraj",
    name: "Savan R G Basavaraj",
    designation: "Assistant Professor",
    deptCode: "CV",
    deptName: "Civil Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/savan-r-g-basavaraj.jpg", alt: "Savan R G Basavaraj, Assistant Professor", width: 1345, height: 1765 },
  },
  {
    slug: "savithri-g-r",
    name: "Savithri G R",
    designation: "Assistant Professor",
    deptCode: "EC",
    deptName: "Electronics & Communication Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/savithri-g-r.jpg", alt: "Savithri G R, Assistant Professor", width: 1991, height: 2560 },
  },
  {
    slug: "supreeth",
    name: "Supreeth",
    designation: "Assistant Professor",
    deptCode: "CV",
    deptName: "Civil Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/supreeth.jpg", alt: "Supreeth, Assistant Professor", width: 400, height: 600 },
  },
  {
    slug: "drusti-s-shastri",
    name: "Drusti S Shastri",
    designation: "Assistant Professor",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/drusti-s-shastri.jpg", alt: "Drusti S Shastri, Assistant Professor", width: 466, height: 442 },
  },
  {
    slug: "priyanka-n",
    name: "Priyanka N",
    designation: "Assistant Professor",
    deptCode: "CV",
    deptName: "Civil Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/priyanka-n.jpg", alt: "Priyanka N, Assistant Professor", width: 1291, height: 1598 },
  },
  {
    slug: "shashank-d-r",
    name: "Shashank D R",
    designation: "Assistant Professor",
    deptCode: "TX",
    deptName: "Textile Technology",
    teaching: true,
    photo: { url: "sites/biet/faculty/shashank-d-r.jpg", alt: "Shashank D R, Assistant Professor", width: 1707, height: 2560 },
  },
  {
    slug: "shashikala-k-j",
    name: "Shashikala K J",
    designation: "Assistant Professor",
    deptCode: "CH",
    deptName: "Chemical Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/shashikala-k-j.jpg", alt: "Shashikala K J, Assistant Professor", width: 202, height: 260 },
  },
  {
    slug: "sushma-b-baraker",
    name: "Sushma B Baraker",
    designation: "Assistant Professor",
    deptCode: "AI",
    deptName: "Artificial Intelligence & Machine Learning",
    teaching: true,
    photo: { url: "sites/biet/faculty/sushma-b-baraker.jpg", alt: "Sushma B Baraker, Assistant Professor", width: 970, height: 886 },
  },
  {
    slug: "arunkumar-p",
    name: "Arunkumar P",
    designation: "Assistant Professor",
    deptCode: "EE",
    deptName: "Electrical & Electronics Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/arunkumar-p.jpg", alt: "Arunkumar P, Assistant Professor", width: 390, height: 508 },
  },
  {
    slug: "priya-s",
    name: "Priya S",
    designation: "Assistant Professor",
    deptCode: "AI",
    deptName: "AI & Machine Learning",
    teaching: true,
    photo: { url: "sites/biet/faculty/priya-s.jpg", alt: "Priya S, Assistant Professor", width: 1071, height: 1280 },
  },
  {
    slug: "nethra-s",
    name: "Nethra S",
    designation: "Assistant Professor of Practice",
    deptCode: "CB",
    deptName: "Computer Science & Business Systems",
    teaching: true,
    photo: { url: "sites/biet/faculty/nethra-s.jpg", alt: "Nethra S, Assistant Professor of Practice", width: 1920, height: 2560 },
  },
  {
    slug: "shruthi-m-t",
    name: "Shruthi M T",
    designation: "Assistant Professor",
    deptCode: "MCA",
    deptName: "Master of Computer Applications",
    teaching: true,
    photo: { url: "sites/biet/faculty/shruthi-m-t.jpg", alt: "Shruthi M T, Assistant Professor", width: 1085, height: 1003 },
  },
  {
    slug: "bilwashri-h",
    name: "Bilwashri H",
    designation: "Assistant Professor",
    deptCode: "BT",
    deptName: "Biotechnology",
    teaching: true,
    photo: { url: "sites/biet/faculty/bilwashri-h.jpg", alt: "Bilwashri H, Assistant Professor", width: 1000, height: 1500 },
  },
  {
    slug: "shubha-v-patel",
    name: "Shubha V Patel",
    designation: "Assistant Professor",
    deptCode: "EC",
    deptName: "Electronics & Communication Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/shubha-v-patel.jpg", alt: "Shubha V Patel, Assistant Professor", width: 1991, height: 2560 },
  },
  {
    slug: "shwetha-s-ec",
    name: "Shwetha S",
    designation: "Assistant Professor",
    deptCode: "EC",
    deptName: "Electronics & Communication Engineering",
    teaching: true,
    // Named on the department's own roster (2026-08-22) but absent from the HR
    // spreadsheet, so no photograph exists yet — a monogram stands in.
    //
    // Dept-code-suffixed slug: a second, unrelated "Shwetha S" in ISS below
    // shared the plain "shwetha-s" slug, which — since it's used as this
    // list's React key — produced duplicate-key warnings and corrupted list
    // reconciliation on the Faculty page (one person's card content bleeding
    // into unrelated rows whenever the department filter changed).
    photo: { url: "", alt: "Shwetha S, Assistant Professor", width: 0, height: 0 },
  },
  {
    slug: "ksheera-p-b",
    name: "Ksheera P B",
    designation: "Assistant Professor",
    deptCode: "CG",
    deptName: "Computer Science & Design",
    teaching: true,
    // Absent from the HR spreadsheet's 394 photographed rows and from every
    // gallery sweep, so she carried a monogram fallback until the department
    // supplied this portrait directly (2026-08-22).
    photo: { url: "sites/biet/faculty/ksheera-p-b.jpg", alt: "Ksheera P B, Assistant Professor", width: 171, height: 221 },
  },
  {
    slug: "shwetha-s-iss",
    name: "Shwetha S",
    designation: "Assistant Professor",
    deptCode: "ISS",
    deptName: "M.Tech (IoT and Sensor Systems)",
    teaching: true,
    photo: { url: "sites/biet/faculty/shwetha-s.jpg", alt: "Shwetha S, Assistant Professor", width: 193, height: 249 },
  },
  {
    slug: "vinayak-p",
    name: "Vinayak P",
    designation: "Assistant Professor",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/vinayak-p.jpg", alt: "Vinayak P, Assistant Professor", width: 780, height: 1020 },
  },
  {
    slug: "siddesh-k-t",
    name: "Siddesh K T",
    designation: "Assistant Professor",
    deptCode: "MCA",
    deptName: "Master of Computer Applications",
    teaching: true,
    photo: { url: "sites/biet/faculty/siddesh-k-t.jpg", alt: "Siddesh K T, Assistant Professor", width: 1251, height: 1650 },
  },
  {
    slug: "sindhu-s-l",
    name: "Sindhu S L",
    designation: "Assistant Professor",
    deptCode: "MCA",
    deptName: "Master of Computer Applications",
    teaching: true,
    photo: { url: "sites/biet/faculty/sindhu-s-l.jpg", alt: "Sindhu S L, Assistant Professor", width: 361, height: 424 },
  },
  {
    slug: "sindhushree-m-h",
    name: "Sindhushree M H",
    designation: "Assistant Professor",
    deptCode: "CG",
    deptName: "Computer Science & Design",
    teaching: true,
    photo: { url: "sites/biet/faculty/sindhushree-m-h.jpg", alt: "Sindhushree M H, Assistant Professor", width: 552, height: 712 },
  },
  {
    slug: "sony-v-hovale",
    name: "Sony V Hovale",
    designation: "Assistant Professor",
    deptCode: "CD",
    deptName: "Computer Science & Engineering (Data Science)",
    teaching: true,
    photo: { url: "sites/biet/faculty/sony-v-hovale.jpg", alt: "Sony V Hovale, Assistant Professor", width: 720, height: 736 },
  },
  {
    slug: "ashwini-a-r",
    name: "Ashwini A R",
    designation: "Assistant Professor",
    deptCode: "EE",
    deptName: "Electrical & Electronics Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/ashwini-a-r.jpg", alt: "Ashwini A R, Assistant Professor", width: 1280, height: 1280 },
  },
  {
    slug: "srinidhi-r-kulkarni",
    name: "Srinidhi R Kulkarni",
    designation: "Assistant Professor",
    deptCode: "CH",
    deptName: "Chemical Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/srinidhi-r-kulkarni.jpg", alt: "Srinidhi R Kulkarni, Assistant Professor", width: 390, height: 510 },
  },
  {
    slug: "suman-b-s",
    name: "Suman B S",
    designation: "Assistant Professor",
    deptCode: "EC",
    deptName: "Electronics & Communication Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/suman-b-s.jpg", alt: "Suman B S, Assistant Professor", width: 126, height: 164 },
  },
  {
    slug: "nidarshana-d-p",
    name: "Nidarshana D P",
    designation: "Assistant Professor",
    deptCode: "CV",
    deptName: "Civil Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/nidarshana-d-p.jpg", alt: "Nidarshana D P, Assistant Professor", width: 199, height: 254 },
  },
  {
    slug: "r-s-chikkangoudar",
    name: "R S Chikkangoudar",
    designation: "Adjunct Professor",
    deptCode: "CV",
    deptName: "Civil Engineering",
    teaching: true,
    // No HR-spreadsheet record; found in the CV gallery sweep, filed under
    // his own name (2026-08-10) and re-ingested here as his faculty photo.
    photo: { url: "sites/biet/faculty/r-s-chikkangoudar.jpg", alt: "R S Chikkangoudar, Adjunct Professor", width: 1991, height: 2560 },
  },
  {
    slug: "supriya-mallad",
    name: "Supriya Mallad",
    designation: "Assistant Professor",
    deptCode: "MCA",
    deptName: "Master of Computer Applications",
    teaching: true,
    photo: { url: "sites/biet/faculty/supriya-mallad.jpg", alt: "Supriya Mallad, Assistant Professor", width: 1180, height: 1496 },
  },
  {
    slug: "ravikumar-h-n",
    name: "Ravikumar H N",
    designation: "Assistant Professor",
    deptCode: "ME",
    deptName: "Mechanical Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/ravikumar-h-n.jpg", alt: "Ravikumar H N, Assistant Professor", width: 1991, height: 2560 },
  },
  {
    slug: "akarsha-k-m",
    name: "Akarsha K M",
    designation: "Assistant Professor",
    deptCode: "AI",
    deptName: "AI & Machine Learning",
    teaching: true,
    photo: { url: "sites/biet/faculty/akarsha-k-m.jpg", alt: "Akarsha K M, Assistant Professor", width: 853, height: 1280 },
  },
  {
    slug: "sahana-n-a",
    name: "Sahana N A",
    designation: "Assistant Professor",
    deptCode: "IS",
    deptName: "Information Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/sahana-n-a.jpg", alt: "Sahana N A, Assistant Professor", width: 2091, height: 2029 },
  },
  {
    slug: "swetha-b",
    name: "Swetha B",
    designation: "Assistant Professor",
    deptCode: "EC",
    deptName: "Electronics & Communication Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/swetha-b.jpg", alt: "Swetha B, Assistant Professor", width: 413, height: 531 },
  },
  {
    slug: "mamatha-s-k",
    name: "Mamatha S K",
    designation: "Assistant Professor",
    deptCode: "AI",
    deptName: "AI & Machine Learning",
    teaching: true,
    photo: { url: "sites/biet/faculty/mamatha-s-k.jpg", alt: "Mamatha S K, Assistant Professor", width: 567, height: 772 },
  },
  {
    slug: "t-k-krishnakumar",
    name: "T K Krishnakumar",
    designation: "Assistant Professor",
    deptCode: "PHY",
    deptName: "Physics",
    teaching: true,
    photo: { url: "sites/biet/faculty/t-k-krishnakumar.jpg", alt: "T K Krishnakumar, Assistant Professor", width: 203, height: 263 },
  },
  {
    slug: "tanushree-m-k",
    name: "Tanushree M K",
    designation: "Assistant Professor",
    deptCode: "PHY",
    deptName: "Physics",
    teaching: true,
    photo: { url: "sites/biet/faculty/tanushree-m-k.jpg", alt: "Tanushree M K, Assistant Professor", width: 1087, height: 1447 },
  },
  {
    slug: "tejashwini-y",
    name: "Tejashwini Y",
    designation: "Assistant Professor",
    deptCode: "EC",
    deptName: "Electronics & Communication Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/tejashwini-y.jpg", alt: "Tejashwini Y, Assistant Professor", width: 867, height: 1156 },
  },
  {
    slug: "thriveni-m-g",
    name: "Thriveni M G",
    designation: "Assistant Professor",
    deptCode: "CHE",
    deptName: "Chemistry",
    teaching: true,
    photo: { url: "sites/biet/faculty/thriveni-m-g.jpg", alt: "Thriveni M G, Assistant Professor", width: 1181, height: 1519 },
  },
  {
    slug: "latha-m",
    name: "Latha M",
    designation: "Assistant Professor",
    deptCode: "CD",
    deptName: "Computer Science & Engineering (Data Science)",
    teaching: true,
    photo: { url: "sites/biet/faculty/latha-m.jpg", alt: "Latha M, Assistant Professor", width: 1200, height: 1600 },
  },
  {
    slug: "v-k-geetha",
    name: "V K Geetha",
    designation: "Assistant Professor",
    deptCode: "PHY",
    deptName: "Physics",
    teaching: true,
    photo: { url: "sites/biet/faculty/v-k-geetha.jpg", alt: "V K Geetha, Assistant Professor", width: 1575, height: 2025 },
  },
  {
    slug: "anusha-a",
    name: "Anusha A",
    designation: "Assistant Professor",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/anusha-a.jpg", alt: "Anusha A, Assistant Professor", width: 1024, height: 1536 },
  },
  {
    slug: "vanishree-h-v",
    name: "Vanishree H V",
    designation: "Assistant Professor",
    deptCode: "EC",
    deptName: "Electronics & Communication Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/vanishree-h-v.jpg", alt: "Vanishree H V, Assistant Professor", width: 500, height: 587 },
  },
  {
    slug: "manasa-m-b",
    name: "Manasa M B",
    designation: "Assistant Professor",
    deptCode: "BT",
    deptName: "Biotechnology",
    teaching: true,
    photo: { url: "sites/biet/faculty/manasa-m-b.jpg", alt: "Manasa M B, Assistant Professor", width: 390, height: 510 },
  },
  {
    slug: "hithaishini-s-g",
    name: "Hithaishini S G",
    designation: "Assistant Professor",
    deptCode: "AI",
    deptName: "AI & Machine Learning",
    teaching: true,
    photo: { url: "sites/biet/faculty/hithaishini-s-g.jpg", alt: "Hithaishini S G, Assistant Professor", width: 864, height: 1316 },
  },
  {
    slug: "sushma-a-patil",
    name: "Sushma A Patil",
    designation: "Assistant Professor",
    deptCode: "ME",
    deptName: "Mechanical Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/sushma-a-patil.jpg", alt: "Sushma A Patil, Assistant Professor", width: 1991, height: 2560 },
  },
  {
    slug: "vikas-k",
    name: "Vikas K",
    designation: "Assistant Professor",
    deptCode: "MAT",
    deptName: "Mathematics",
    teaching: true,
    photo: { url: "sites/biet/faculty/vikas-k.jpg", alt: "Vikas K, Assistant Professor", width: 1920, height: 2560 },
  },
  {
    slug: "vinay-parol",
    name: "Vinay Parol",
    designation: "Assistant Professor",
    deptCode: "PHY",
    deptName: "Physics",
    teaching: true,
    photo: { url: "sites/biet/faculty/vinay-parol.jpg", alt: "Vinay Parol, Assistant Professor", width: 883, height: 840 },
  },
  {
    slug: "vinay-patel-g-l",
    name: "Vinay Patel G L",
    designation: "Assistant Professor",
    deptCode: "MCA",
    deptName: "Master of Computer Applications",
    teaching: true,
    photo: { url: "sites/biet/faculty/vinay-patel-g-l.jpg", alt: "Vinay Patel G L, Assistant Professor", width: 1314, height: 1602 },
  },
  {
    slug: "vinutha-k-p",
    name: "Vinutha K P",
    designation: "Assistant Professor",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/vinutha-k-p.jpg", alt: "Vinutha K P, Assistant Professor", width: 780, height: 1020 },
  },
  {
    slug: "manjunatha-g-m",
    name: "Manjunatha G M",
    designation: "Assistant Professor",
    deptCode: "ME",
    deptName: "Mechanical Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/manjunatha-g-m.jpg", alt: "Manjunatha G M, Assistant Professor", width: 1991, height: 2560 },
  },
  {
    slug: "parveen-taj-s",
    name: "Parveen Taj S",
    designation: "Assistant Professor",
    deptCode: "IS",
    deptName: "Information Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/parveen-taj-s.jpg", alt: "Parveen Taj S, Assistant Professor", width: 1104, height: 1456 },
  },
  {
    slug: "vinutha-d-k",
    name: "Vinutha D K",
    designation: "Assistant Professor",
    deptCode: "MCA",
    deptName: "Master of Computer Applications",
    teaching: true,
    photo: { url: "sites/biet/faculty/vinutha-d-k.jpg", alt: "Vinutha D K, Assistant Professor", width: 558, height: 705 },
  },
  {
    slug: "sanjay",
    name: "Sanjay",
    designation: "Assistant Professor",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/sanjay.jpg", alt: "Sanjay, Assistant Professor", width: 780, height: 1020 },
  },
  {
    slug: "vishalakshi-a-b",
    name: "Vishalakshi A B",
    designation: "Assistant Professor",
    deptCode: "MAT",
    deptName: "Mathematics",
    teaching: true,
    photo: { url: "sites/biet/faculty/vishalakshi-a-b.jpg", alt: "Vishalakshi A B, Assistant Professor", width: 1086, height: 1448 },
  },
  {
    slug: "khushi-v-s",
    name: "Khushi V S",
    designation: "Assistant Professor",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/khushi-v-s.jpg", alt: "Khushi V S, Assistant Professor", width: 780, height: 1020 },
  },
  {
    slug: "vani-h-u",
    name: "Vani H U",
    designation: "Assistant Professor",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/vani-h-u.jpg", alt: "Vani H U, Assistant Professor", width: 714, height: 947 },
  },
  {
    slug: "yashodha-puttappa-ganji",
    name: "Yashodha Puttappa Ganji",
    designation: "Assistant Professor",
    deptCode: "MCA",
    deptName: "Master of Computer Applications",
    teaching: true,
    photo: { url: "sites/biet/faculty/yashodha-puttappa-ganji.jpg", alt: "Yashodha Puttappa Ganji, Assistant Professor", width: 731, height: 619 },
  },
  {
    slug: "yogesha-k-o",
    name: "Yogesha K O",
    designation: "Assistant Professor",
    deptCode: "EC",
    deptName: "Electronics & Communication Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/yogesha-k-o.jpg", alt: "Yogesha K O, Assistant Professor", width: 437, height: 555 },
  },
  {
    slug: "priyanka-p",
    name: "Priyanka P",
    designation: "Assistant Professor",
    deptCode: "CB",
    deptName: "Computer Science & Business Systems",
    teaching: true,
    photo: { url: "sites/biet/faculty/priyanka-p.jpg", alt: "Priyanka P, Assistant Professor", width: 600, height: 600 },
  },
  {
    slug: "annapurneshwari-p",
    name: "Annapurneshwari P",
    designation: "Lecturer",
    deptCode: "SH",
    deptName: "Humanities",
    teaching: true,
    photo: { url: "sites/biet/faculty/annapurneshwari-p.jpg", alt: "Annapurneshwari P, Lecturer", width: 201, height: 260 },
  },
  {
    slug: "m-c-patel",
    name: "M C Patel",
    designation: "Lecturer",
    deptCode: "TX",
    deptName: "Textile Technology",
    teaching: true,
    photo: { url: "sites/biet/faculty/m-c-patel.jpg", alt: "M C Patel, Lecturer", width: 253, height: 271 },
  },
  {
    slug: "mahendrachari",
    name: "Mahendrachari",
    designation: "Lecturer",
    deptCode: "EC",
    deptName: "Electronics & Communication Engineering",
    teaching: true,
    photo: { url: "sites/biet/faculty/mahendrachari.jpg", alt: "Mahendrachari, Lecturer", width: 1960, height: 1824 },
  },
  {
    slug: "y-vrushabhendrappa",
    name: "Y Vrushabhendrappa",
    designation: "Director",
    deptCode: "Office",
    deptName: "Administrative Office",
    teaching: false,
    photo: { url: "sites/biet/faculty/y-vrushabhendrappa.jpg", alt: "Y Vrushabhendrappa, Director", width: 2560, height: 1706 },
  },
  {
    slug: "t-c-rajashekhar",
    name: "T C Rajashekhar",
    designation: "Manager",
    deptCode: "Office",
    deptName: "Administrative Office",
    teaching: false,
    photo: { url: "sites/biet/faculty/t-c-rajashekhar.jpg", alt: "T C Rajashekhar, Manager", width: 1991, height: 2560 },
  },
  {
    slug: "k-v-manjunatha",
    name: "K V Manjunatha",
    designation: "Librarian",
    deptCode: "Library",
    deptName: "Library",
    teaching: false,
    photo: { url: "sites/biet/faculty/k-v-manjunatha.jpg", alt: "K V Manjunatha, Librarian", width: 270, height: 350 },
  },
  {
    slug: "g-s-shakunthala",
    name: "G S Shakunthala",
    designation: "Assistant Librarian",
    deptCode: "Library",
    deptName: "Library",
    teaching: false,
    photo: { url: "sites/biet/faculty/g-s-shakunthala.jpg", alt: "G S Shakunthala, Assistant Librarian", width: 878, height: 1180 },
  },
  {
    slug: "bheemappa-l-k",
    name: "Bheemappa L K",
    designation: "Garden Supervisor",
    deptCode: "Mantainances",
    deptName: "Maintenance",
    teaching: false,
    photo: { url: "sites/biet/faculty/bheemappa-l-k.jpg", alt: "Bheemappa L K, Garden Supervisor", width: 1448, height: 1840 },
  },
  {
    slug: "gangadharappa-a-c",
    name: "Gangadharappa A C",
    designation: "Programmer",
    deptCode: "MMD",
    deptName: "MMD",
    teaching: false,
    photo: { url: "sites/biet/faculty/gangadharappa-a-c.jpg", alt: "Gangadharappa A C, Programmer", width: 1118, height: 1600 },
  },
  {
    slug: "indira-s-h",
    name: "Indira S H",
    designation: "Programmer",
    deptCode: "IS",
    deptName: "Information Science & Engineering",
    teaching: false,
    photo: { url: "sites/biet/faculty/indira-s-h.jpg", alt: "Indira S H, Programmer", width: 1024, height: 1316 },
  },
  {
    slug: "m-p-nataraj",
    name: "M P Nataraj",
    designation: "Programmer",
    deptCode: "MCA",
    deptName: "Master of Computer Applications",
    teaching: false,
    photo: { url: "sites/biet/faculty/m-p-nataraj.jpg", alt: "M P Nataraj, Programmer", width: 295, height: 330 },
  },
  {
    slug: "jayadeva-j-s",
    name: "Jayadeva J S",
    designation: "Assistant Programmer",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: false,
    photo: { url: "sites/biet/faculty/jayadeva-j-s.jpg", alt: "Jayadeva J S, Assistant Programmer", width: 270, height: 350 },
  },
  {
    slug: "abhishek-j-j",
    name: "Abhishek J J",
    designation: "Foreman",
    deptCode: "AI",
    deptName: "AI & Machine Learning",
    teaching: false,
    photo: { url: "sites/biet/faculty/abhishek-j-j.jpg", alt: "Abhishek J J, Foreman", width: 1099, height: 1089 },
  },
  {
    slug: "k-jagadeesh",
    name: "K Jagadeesh",
    designation: "Foreman",
    deptCode: "",
    deptName: "",
    teaching: false,
    photo: { url: "sites/biet/faculty/k-jagadeesh.jpg", alt: "K Jagadeesh, Foreman", width: 1991, height: 2560 },
  },
  {
    slug: "amrutha-s",
    name: "Amrutha S",
    designation: "Instructor",
    deptCode: "AI",
    deptName: "AI & Machine Learning",
    teaching: false,
    photo: { url: "sites/biet/faculty/amrutha-s.jpg", alt: "Amrutha S, Instructor", width: 386, height: 528 },
  },
  {
    slug: "pramod-h",
    name: "Pramod H",
    designation: "Instructor",
    deptCode: "CD",
    deptName: "Computer Science & Engineering (Data Science)",
    teaching: false,
    photo: { url: "sites/biet/faculty/pramod-h.jpg", alt: "Pramod H, Instructor", width: 1020, height: 1360 },
  },
  {
    slug: "anusha-c",
    name: "Anusha C",
    designation: "Instructor",
    deptCode: "EC",
    deptName: "Electronics & Communication Engineering",
    teaching: false,
    photo: { url: "sites/biet/faculty/anusha-c.jpg", alt: "Anusha C, Instructor", width: 402, height: 532 },
  },
  {
    slug: "ashwini-s-h",
    name: "Ashwini S H",
    designation: "Instructor",
    deptCode: "CV",
    deptName: "Civil Engineering",
    teaching: false,
    photo: { url: "sites/biet/faculty/ashwini-s-h.jpg", alt: "Ashwini S H, Instructor", width: 413, height: 531 },
  },
  {
    slug: "chethan-b-k",
    name: "Chethan B K",
    designation: "Instructor",
    deptCode: "CB",
    deptName: "Computer Science & Business Systems",
    teaching: false,
    photo: { url: "sites/biet/faculty/chethan-b-k.jpg", alt: "Chethan B K, Instructor", width: 462, height: 617 },
  },
  {
    slug: "chethan-k-p-r",
    name: "Chethan K P R",
    designation: "Instructor",
    deptCode: "AI",
    deptName: "Artificial Intelligence & Machine Learning",
    teaching: false,
    photo: { url: "sites/biet/faculty/chethan-k-p-r.jpg", alt: "Chethan K P R, Instructor", width: 433, height: 576 },
  },
  {
    slug: "deepashree-s-horapeti",
    name: "Deepashree S Horapeti",
    designation: "Instructor",
    deptCode: "TX",
    deptName: "Textile Technology",
    teaching: false,
    photo: { url: "sites/biet/faculty/deepashree-s-horapeti.jpg", alt: "Deepashree S Horapeti, Instructor", width: 1672, height: 2060 },
  },
  {
    slug: "m-s-deepak",
    name: "M S Deepak",
    designation: "Instructor",
    deptCode: "EE",
    deptName: "Electrical & Electronics Engineering",
    teaching: false,
    photo: { url: "sites/biet/faculty/m-s-deepak.jpg", alt: "M S Deepak, Instructor", width: 500, height: 300 },
  },
  {
    slug: "mahaboob-basha-m-d",
    name: "Mahaboob Basha M D",
    designation: "Instructor",
    deptCode: "EE",
    deptName: "Electrical & Electronics Engineering",
    teaching: false,
    photo: { url: "sites/biet/faculty/mahaboob-basha-m-d.jpg", alt: "Mahaboob Basha M D, Instructor", width: 1204, height: 1549 },
  },
  {
    slug: "mahesh-k-b",
    name: "Mahesh K B",
    designation: "Instructor",
    deptCode: "CHE",
    deptName: "Chemistry",
    teaching: false,
    photo: { url: "sites/biet/faculty/mahesh-k-b.jpg", alt: "Mahesh K B, Instructor", width: 933, height: 1184 },
  },
  {
    slug: "naveenkumar-c-s",
    name: "Naveenkumar C S",
    designation: "Instructor",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: false,
    photo: { url: "sites/biet/faculty/naveenkumar-c-s.jpg", alt: "Naveenkumar C S, Instructor", width: 270, height: 350 },
  },
  {
    slug: "naresh-kumar-c",
    name: "Naresh Kumar C",
    designation: "Instructor",
    deptCode: "CV",
    deptName: "Civil Engineering",
    teaching: false,
    photo: { url: "sites/biet/faculty/naresh-kumar-c.jpg", alt: "Naresh Kumar C, Instructor", width: 501, height: 577 },
  },
  {
    slug: "narendra-b-o",
    name: "Narendra B O",
    designation: "Instructor",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: false,
    photo: { url: "sites/biet/faculty/narendra-b-o.jpg", alt: "Narendra B O, Instructor", width: 780, height: 1020 },
  },
  {
    slug: "pradeep-s-k",
    name: "Pradeep S K",
    designation: "Instructor",
    deptCode: "CV",
    deptName: "Civil Engineering",
    teaching: false,
    photo: { url: "sites/biet/faculty/pradeep-s-k.jpg", alt: "Pradeep S K, Instructor", width: 270, height: 350 },
  },
  {
    slug: "priya-h-s",
    name: "Priya H S",
    designation: "Instructor",
    deptCode: "IS",
    deptName: "Information Science & Engineering",
    teaching: false,
    photo: { url: "sites/biet/faculty/priya-h-s.jpg", alt: "Priya H S, Instructor", width: 1800, height: 2400 },
  },
  {
    slug: "s-p-umapathy",
    name: "S P Umapathy",
    designation: "Instructor",
    deptCode: "EC",
    deptName: "Electronics & Communication Engineering",
    teaching: false,
    photo: { url: "sites/biet/faculty/s-p-umapathy.jpg", alt: "S P Umapathy, Instructor", width: 1991, height: 2560 },
  },
  {
    slug: "s-siddeshi",
    name: "S Siddeshi",
    designation: "Instructor",
    deptCode: "IS",
    deptName: "Information Science & Engineering",
    teaching: false,
    photo: { url: "sites/biet/faculty/s-siddeshi.jpg", alt: "S Siddeshi, Instructor", width: 451, height: 580 },
  },
  {
    slug: "u-m-suresh",
    name: "U M Suresh",
    designation: "Instructor",
    deptCode: "ME",
    deptName: "Mechanical Engineering",
    teaching: false,
    photo: { url: "sites/biet/faculty/u-m-suresh.jpg", alt: "U M Suresh, Instructor", width: 827, height: 1063 },
  },
  {
    slug: "v-k-mallikarjuna",
    name: "V K Mallikarjuna",
    designation: "Instructor",
    deptCode: "ME",
    deptName: "Mechanical Engineering",
    teaching: false,
    photo: { url: "sites/biet/faculty/v-k-mallikarjuna.jpg", alt: "V K Mallikarjuna, Instructor", width: 898, height: 1241 },
  },
  {
    slug: "v-s-patil",
    name: "V S Patil",
    designation: "Instructor",
    deptCode: "EC",
    deptName: "Electronics & Communication Engineering",
    teaching: false,
    photo: { url: "sites/biet/faculty/v-s-patil.jpg", alt: "V S Patil, Instructor", width: 331, height: 406 },
  },
  {
    slug: "vinutha-k-l",
    name: "Vinutha K L",
    designation: "Instructor",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: false,
    photo: { url: "sites/biet/faculty/vinutha-k-l.jpg", alt: "Vinutha K L, Instructor", width: 780, height: 1020 },
  },
  {
    slug: "rahul-patil-g-b",
    name: "Rahul Patil G B",
    designation: "Instructor",
    deptCode: "BT",
    deptName: "Biotechnology",
    teaching: false,
    photo: { url: "sites/biet/faculty/rahul-patil-g-b.jpg", alt: "Rahul Patil G B, Instructor", width: 314, height: 340 },
  },
  {
    slug: "annapurnamma-k",
    name: "Annapurnamma K",
    designation: "Assistant Instructor",
    deptCode: "CV",
    deptName: "Civil Engineering",
    teaching: false,
    photo: { url: "sites/biet/faculty/annapurnamma-k.jpg", alt: "Annapurnamma K, Assistant Instructor", width: 909, height: 811 },
  },
  {
    slug: "girish-m",
    name: "Girish M",
    designation: "Assistant Instructor",
    deptCode: "EC",
    deptName: "Electronics & Communication Engineering",
    teaching: false,
    photo: { url: "sites/biet/faculty/girish-m.jpg", alt: "Girish M, Assistant Instructor", width: 1270, height: 1600 },
  },
  {
    slug: "mruthyunjaya-k",
    name: "Mruthyunjaya K",
    designation: "Assistant Instructor",
    deptCode: "ME",
    deptName: "Mechanical Engineering",
    teaching: false,
    photo: { url: "sites/biet/faculty/mruthyunjaya-k.jpg", alt: "Mruthyunjaya K, Assistant Instructor", width: 1991, height: 2560 },
  },
  {
    slug: "shivaraju-g-n",
    name: "Shivaraju G N",
    designation: "Assistant Instructor",
    deptCode: "ME",
    deptName: "Mechanical Engineering",
    teaching: false,
    photo: { url: "sites/biet/faculty/shivaraju-g-n.jpg", alt: "Shivaraju G N, Assistant Instructor", width: 2120, height: 2560 },
  },
  {
    slug: "t-gururaj",
    name: "T Gururaj",
    designation: "Assistant Instructor",
    deptCode: "CH",
    deptName: "Chemical Engineering",
    teaching: false,
    photo: { url: "sites/biet/faculty/t-gururaj.jpg", alt: "T Gururaj, Assistant Instructor", width: 1991, height: 2560 },
  },
  {
    slug: "badami-muppesh",
    name: "Badami Muppesh",
    designation: "Physical Instructor",
    deptCode: "Sports",
    deptName: "Physical Education",
    teaching: false,
    photo: { url: "sites/biet/faculty/badami-muppesh.jpg", alt: "Badami Muppesh, Physical Instructor", width: 709, height: 832 },
  },
  {
    slug: "b-v-nagendrachari",
    name: "B V Nagendrachari",
    designation: "Mechanic",
    deptCode: "",
    deptName: "",
    teaching: false,
    photo: { url: "sites/biet/faculty/b-v-nagendrachari.jpg", alt: "B V Nagendrachari, Mechanic", width: 1991, height: 2560 },
  },
  {
    slug: "k-r-vedamurthy",
    name: "K R Vedamurthy",
    designation: "Mechanic",
    deptCode: "ME",
    deptName: "Mechanical Engineering",
    teaching: false,
    photo: { url: "sites/biet/faculty/k-r-vedamurthy.jpg", alt: "K R Vedamurthy, Mechanic", width: 1991, height: 2560 },
  },
  {
    slug: "lingaraju-k",
    name: "Lingaraju  K",
    designation: "Mechanic",
    deptCode: "Library",
    deptName: "Library",
    teaching: false,
    photo: { url: "sites/biet/faculty/lingaraju-k.jpg", alt: "Lingaraju  K, Mechanic", width: 960, height: 1280 },
  },
  {
    slug: "yuvaraja-t-m",
    name: "Yuvaraja T M",
    designation: "Mechanic",
    deptCode: "CG",
    deptName: "Computer Science & Design",
    teaching: false,
    photo: { url: "sites/biet/faculty/yuvaraja-t-m.jpg", alt: "Yuvaraja T M, Mechanic", width: 1240, height: 1600 },
  },
  {
    slug: "jagadeesha-a-s",
    name: "Jagadeesha A S",
    designation: "Assistant Mechanic",
    deptCode: "Office",
    deptName: "Administrative Office",
    teaching: false,
    photo: { url: "sites/biet/faculty/jagadeesha-a-s.jpg", alt: "Jagadeesha A S, Assistant Mechanic", width: 2560, height: 1707 },
  },
  {
    slug: "praveen-kumar-n-r",
    name: "Praveen Kumar   N R",
    designation: "Electrician",
    deptCode: "EE",
    deptName: "Electrical & Electronics Engineering",
    teaching: false,
    photo: { url: "sites/biet/faculty/praveen-kumar-n-r.jpg", alt: "Praveen Kumar   N R, Electrician", width: 1305, height: 1328 },
  },
  {
    slug: "c-basavarajaiah",
    name: "C Basavarajaiah",
    designation: "Fitter",
    deptCode: "TX",
    deptName: "Textile Technology",
    teaching: false,
    photo: { url: "sites/biet/faculty/c-basavarajaiah.jpg", alt: "C Basavarajaiah, Fitter", width: 730, height: 748 },
  },
  {
    slug: "babajan-s",
    name: "Babajan S",
    designation: "Machine Operator",
    deptCode: "SDC",
    deptName: "Skill Development Cell",
    teaching: false,
    photo: { url: "sites/biet/faculty/babajan-s.jpg", alt: "Babajan S, Machine Operator", width: 706, height: 969 },
  },
  {
    slug: "amrutesh-b-r",
    name: "Amrutesh B R",
    designation: "FDA",
    deptCode: "",
    deptName: "",
    teaching: false,
    photo: { url: "sites/biet/faculty/amrutesh-b-r.jpg", alt: "Amrutesh B R, FDA", width: 1534, height: 1919 },
  },
  {
    slug: "anusha-k-e",
    name: "Anusha  K E",
    designation: "FDA",
    deptCode: "Office",
    deptName: "Administrative Office",
    teaching: false,
    photo: { url: "sites/biet/faculty/anusha-k-e.jpg", alt: "Anusha  K E, FDA", width: 285, height: 273 },
  },
  {
    slug: "arunakumar-k-c",
    name: "Arunakumar   K C",
    designation: "FDA",
    deptCode: "Office",
    deptName: "Administrative Office",
    teaching: false,
    photo: { url: "sites/biet/faculty/arunakumar-k-c.jpg", alt: "Arunakumar   K C, FDA", width: 1991, height: 2560 },
  },
  {
    slug: "b-v-manjunatha",
    name: "B V Manjunatha",
    designation: "FDA",
    deptCode: "Office",
    deptName: "Administrative Office",
    teaching: false,
    photo: { url: "sites/biet/faculty/b-v-manjunatha.jpg", alt: "B V Manjunatha, FDA", width: 1597, height: 2129 },
  },
  {
    slug: "bhagyamma-s",
    name: "Bhagyamma S",
    designation: "FDA",
    deptCode: "Office",
    deptName: "Administrative Office",
    teaching: false,
    photo: { url: "sites/biet/faculty/bhagyamma-s.jpg", alt: "Bhagyamma S, FDA", width: 468, height: 511 },
  },
  {
    slug: "h-ajjaiah",
    name: "H Ajjaiah",
    designation: "FDA",
    deptCode: "Office",
    deptName: "Administrative Office",
    teaching: false,
    photo: { url: "sites/biet/faculty/h-ajjaiah.jpg", alt: "H Ajjaiah, FDA", width: 1500, height: 1929 },
  },
  {
    slug: "hareesha-m-r",
    name: "Hareesha  M R",
    designation: "FDA",
    deptCode: "Office",
    deptName: "Administrative Office",
    teaching: false,
    photo: { url: "sites/biet/faculty/hareesha-m-r.jpg", alt: "Hareesha  M R, FDA", width: 1027, height: 1269 },
  },
  {
    slug: "nagaraj-halabhavi",
    name: "Nagaraj Halabhavi",
    designation: "FDA",
    deptCode: "Office",
    deptName: "Administrative Office",
    teaching: false,
    photo: { url: "sites/biet/faculty/nagaraj-halabhavi.jpg", alt: "Nagaraj Halabhavi, FDA", width: 1063, height: 1241 },
  },
  {
    slug: "prashanth-b",
    name: "Prashanth B",
    designation: "FDA",
    deptCode: "Office",
    deptName: "Administrative Office",
    teaching: false,
    photo: { url: "sites/biet/faculty/prashanth-b.jpg", alt: "Prashanth B, FDA", width: 986, height: 1280 },
  },
  {
    slug: "pushpalatha-h-k",
    name: "Pushpalatha H K",
    designation: "FDA",
    deptCode: "Office",
    deptName: "Administrative Office",
    teaching: false,
    photo: { url: "sites/biet/faculty/pushpalatha-h-k.jpg", alt: "Pushpalatha H K, FDA", width: 1991, height: 2560 },
  },
  {
    slug: "siranahalli-ajjappa-raja",
    name: "Siranahalli Ajjappa Raja",
    designation: "FDA",
    deptCode: "Office",
    deptName: "Administrative Office",
    teaching: false,
    photo: { url: "sites/biet/faculty/siranahalli-ajjappa-raja.jpg", alt: "Siranahalli Ajjappa Raja, FDA", width: 1991, height: 2560 },
  },
  {
    slug: "vasanthakumari",
    name: "Vasanthakumari",
    designation: "FDA",
    deptCode: "Office",
    deptName: "Administrative Office",
    teaching: false,
    photo: { url: "sites/biet/faculty/vasanthakumari.jpg", alt: "Vasanthakumari, FDA", width: 1991, height: 2560 },
  },
  {
    slug: "c-diwakar",
    name: "C Diwakar",
    designation: "SDA",
    deptCode: "Office",
    deptName: "Administrative Office",
    teaching: false,
    photo: { url: "sites/biet/faculty/c-diwakar.jpg", alt: "C Diwakar, SDA", width: 498, height: 471 },
  },
  {
    slug: "jagadeesh-j",
    name: "Jagadeesh J",
    designation: "SDA",
    deptCode: "Office",
    deptName: "Administrative Office",
    teaching: false,
    photo: { url: "sites/biet/faculty/jagadeesh-j.jpg", alt: "Jagadeesh J, SDA", width: 824, height: 1026 },
  },
  {
    slug: "pavankumar-d-s",
    name: "Pavankumar D S",
    designation: "SDA",
    deptCode: "Office",
    deptName: "Administrative Office",
    teaching: false,
    photo: { url: "sites/biet/faculty/pavankumar-d-s.jpg", alt: "Pavankumar D S, SDA", width: 1246, height: 1600 },
  },
  {
    slug: "manjunatha-r",
    name: "Manjunatha R",
    designation: "Driver",
    deptCode: "Office",
    deptName: "Administrative Office",
    teaching: false,
    photo: { url: "sites/biet/faculty/manjunatha-r.jpg", alt: "Manjunatha R, Driver", width: 368, height: 488 },
  },
  {
    slug: "r-manjunath-em1469",
    name: "R Manjunath",
    designation: "Driver",
    deptCode: "",
    deptName: "",
    teaching: false,
    photo: { url: "sites/biet/faculty/r-manjunath-em1469.jpg", alt: "R Manjunath, Driver", width: 829, height: 1023 },
  },
  {
    slug: "m-b-nagaraja",
    name: "M B Nagaraja",
    designation: "Lab Attender",
    deptCode: "TX",
    deptName: "Textile Technology",
    teaching: false,
    photo: { url: "sites/biet/faculty/m-b-nagaraja.jpg", alt: "M B Nagaraja, Lab Attender", width: 978, height: 1280 },
  },
  {
    slug: "g-ramesh",
    name: "G Ramesh",
    designation: "Attender",
    deptCode: "EC",
    deptName: "Electronics & Communication Engineering",
    teaching: false,
    photo: { url: "sites/biet/faculty/g-ramesh.jpg", alt: "G Ramesh, Attender", width: 1244, height: 1600 },
  },
  {
    slug: "jagadish-a",
    name: "Jagadish A",
    designation: "Attender",
    deptCode: "ME",
    deptName: "Mechanical Engineering",
    teaching: false,
    photo: { url: "sites/biet/faculty/jagadish-a.jpg", alt: "Jagadish A, Attender", width: 1991, height: 2560 },
  },
  {
    slug: "kunneshi",
    name: "Kunneshi",
    designation: "Attender",
    deptCode: "CV",
    deptName: "Civil Engineering",
    teaching: false,
    photo: { url: "sites/biet/faculty/kunneshi.jpg", alt: "Kunneshi, Attender", width: 960, height: 1152 },
  },
  {
    slug: "nagaraja-a",
    name: "Nagaraja A",
    designation: "Attender",
    deptCode: "Library",
    deptName: "Library",
    teaching: false,
    photo: { url: "sites/biet/faculty/nagaraja-a.jpg", alt: "Nagaraja A, Attender", width: 403, height: 529 },
  },
  {
    slug: "r-manjunath-em1266",
    name: "R Manjunath",
    designation: "Attender",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: false,
    photo: { url: "sites/biet/faculty/r-manjunath-em1266.jpg", alt: "R Manjunath, Attender", width: 133, height: 152 },
  },
  {
    slug: "s-pushpa",
    name: "S Pushpa",
    designation: "Attender",
    deptCode: "Library",
    deptName: "Library",
    teaching: false,
    photo: { url: "sites/biet/faculty/s-pushpa.jpg", alt: "S Pushpa, Attender", width: 398, height: 473 },
  },
  {
    slug: "shanthaiah-t-m",
    name: "Shanthaiah T M",
    designation: "Attender",
    deptCode: "Office",
    deptName: "Administrative Office",
    teaching: false,
    photo: { url: "sites/biet/faculty/shanthaiah-t-m.jpg", alt: "Shanthaiah T M, Attender", width: 591, height: 761 },
  },
  {
    slug: "b-s-suresh",
    name: "B S Suresh",
    designation: "Peon",
    deptCode: "CG",
    deptName: "Computer Science & Design",
    teaching: false,
    photo: { url: "sites/biet/faculty/b-s-suresh.jpg", alt: "B S Suresh, Peon", width: 270, height: 350 },
  },
  {
    slug: "bakkeshwara-g",
    name: "Bakkeshwara G",
    designation: "Peon",
    deptCode: "IS",
    deptName: "Information Science & Engineering",
    teaching: false,
    photo: { url: "sites/biet/faculty/bakkeshwara-g.jpg", alt: "Bakkeshwara G, Peon", width: 1024, height: 1316 },
  },
  {
    slug: "c-b-patil",
    name: "C B Patil",
    designation: "Peon",
    deptCode: "PHY",
    deptName: "Physics",
    teaching: false,
    photo: { url: "sites/biet/faculty/c-b-patil.jpg", alt: "C B Patil, Peon", width: 1575, height: 2025 },
  },
  {
    slug: "channabasappa-k",
    name: "Channabasappa K",
    designation: "Peon",
    deptCode: "",
    deptName: "",
    teaching: false,
    photo: { url: "sites/biet/faculty/channabasappa-k.jpg", alt: "Channabasappa K, Peon", width: 815, height: 1050 },
  },
  {
    slug: "d-s-punith",
    name: "D S Punith",
    designation: "Peon",
    deptCode: "CV",
    deptName: "Civil Engineering",
    teaching: false,
    photo: { url: "sites/biet/faculty/d-s-punith.jpg", alt: "D S Punith, Peon", width: 1080, height: 1533 },
  },
  {
    slug: "govindaraju-h",
    name: "Govindaraju H",
    designation: "Peon",
    deptCode: "Library",
    deptName: "Library",
    teaching: false,
    photo: { url: "sites/biet/faculty/govindaraju-h.jpg", alt: "Govindaraju H, Peon", width: 1015, height: 1280 },
  },
  {
    slug: "gurupada-gouda",
    name: "Gurupada Gouda",
    designation: "Peon",
    deptCode: "PHY",
    deptName: "Physics",
    teaching: false,
    photo: { url: "sites/biet/faculty/gurupada-gouda.jpg", alt: "Gurupada Gouda, Peon", width: 705, height: 974 },
  },
  {
    slug: "h-ningappa",
    name: "H Ningappa",
    designation: "Peon",
    deptCode: "ME",
    deptName: "Mechanical Engineering",
    teaching: false,
    photo: { url: "sites/biet/faculty/h-ningappa.jpg", alt: "H Ningappa, Peon", width: 1991, height: 2560 },
  },
  {
    slug: "hanumantha-h",
    name: "Hanumantha H",
    designation: "Peon",
    deptCode: "Mantainances",
    deptName: "Maintenance",
    teaching: false,
    photo: { url: "sites/biet/faculty/hanumantha-h.jpg", alt: "Hanumantha H, Peon", width: 1207, height: 1600 },
  },
  {
    slug: "k-r-narappa",
    name: "K R Narappa",
    designation: "Peon",
    deptCode: "CV",
    deptName: "Civil Engineering",
    teaching: false,
    photo: { url: "sites/biet/faculty/k-r-narappa.jpg", alt: "K R Narappa, Peon", width: 812, height: 812 },
  },
  {
    slug: "k-s-dhanyakumar",
    name: "K S Dhanyakumar",
    designation: "Peon",
    deptCode: "",
    deptName: "",
    teaching: false,
    photo: { url: "sites/biet/faculty/k-s-dhanyakumar.jpg", alt: "K S Dhanyakumar, Peon", width: 1345, height: 1709 },
  },
  {
    slug: "k-s-santosh",
    name: "K S Santosh",
    designation: "Peon",
    deptCode: "Office",
    deptName: "Administrative Office",
    teaching: false,
    photo: { url: "sites/biet/faculty/k-s-santosh.jpg", alt: "K S Santosh, Peon", width: 620, height: 581 },
  },
  {
    slug: "kenchappa-n",
    name: "Kenchappa N",
    designation: "Peon",
    deptCode: "CB",
    deptName: "Computer Science & Business Systems",
    teaching: false,
    photo: { url: "sites/biet/faculty/kenchappa-n.jpg", alt: "Kenchappa N, Peon", width: 1023, height: 1600 },
  },
  {
    slug: "kiran-r",
    name: "Kiran R",
    designation: "Peon",
    deptCode: "Office",
    deptName: "Administrative Office",
    teaching: false,
    photo: { url: "sites/biet/faculty/kiran-r.jpg", alt: "Kiran R, Peon", width: 720, height: 1560 },
  },
  {
    slug: "kumar-k-b",
    name: "Kumar K B",
    designation: "Peon",
    deptCode: "Mantainances",
    deptName: "Maintenance",
    teaching: false,
    photo: { url: "sites/biet/faculty/kumar-k-b.jpg", alt: "Kumar K B, Peon", width: 333, height: 449 },
  },
  {
    slug: "mahamadali-godikar",
    name: "Mahamadali Godikar",
    designation: "Peon",
    deptCode: "MCA",
    deptName: "Master of Computer Applications",
    teaching: false,
    photo: { url: "sites/biet/faculty/mahamadali-godikar.jpg", alt: "Mahamadali Godikar, Peon", width: 1062, height: 1288 },
  },
  {
    slug: "mallikarjuna-a-p",
    name: "Mallikarjuna A P",
    designation: "Peon",
    deptCode: "CH",
    deptName: "Chemical Engineering",
    teaching: false,
    photo: { url: "sites/biet/faculty/mallikarjuna-a-p.jpg", alt: "Mallikarjuna A P, Peon", width: 880, height: 1106 },
  },
  {
    slug: "manjunath-k-b",
    name: "Manjunath K B",
    designation: "Peon",
    deptCode: "Mantainances",
    deptName: "Maintenance",
    teaching: false,
    photo: { url: "sites/biet/faculty/manjunath-k-b.jpg", alt: "Manjunath K B, Peon", width: 1080, height: 1824 },
  },
  {
    slug: "manjunath-n",
    name: "Manjunath N",
    designation: "Peon",
    deptCode: "CS",
    deptName: "Computer Science & Engineering",
    teaching: false,
    photo: { url: "sites/biet/faculty/manjunath-n.jpg", alt: "Manjunath N, Peon", width: 396, height: 516 },
  },
  {
    slug: "thimmeshi-h-g",
    name: "Thimmeshi H G",
    designation: "Peon",
    deptCode: "CD",
    deptName: "Computer Science & Engineering (Data Science)",
    teaching: false,
    photo: { url: "sites/biet/faculty/thimmeshi-h-g.jpg", alt: "Thimmeshi H G, Peon", width: 1338, height: 1461 },
  },
  {
    slug: "nirmala-k-p",
    name: "Nirmala K P",
    designation: "Peon",
    deptCode: "Library",
    deptName: "Library",
    teaching: false,
    photo: { url: "sites/biet/faculty/nirmala-k-p.jpg", alt: "Nirmala K P, Peon", width: 1098, height: 1337 },
  },
  {
    slug: "p-m-channabasamma",
    name: "P M Channabasamma",
    designation: "Peon",
    deptCode: "TX",
    deptName: "Textile Technology",
    teaching: false,
    photo: { url: "sites/biet/faculty/p-m-channabasamma.jpg", alt: "P M Channabasamma, Peon", width: 1028, height: 1280 },
  },
  {
    slug: "p-rudreshi",
    name: "P Rudreshi",
    designation: "Peon",
    deptCode: "MAT",
    deptName: "Mathematics",
    teaching: false,
    photo: { url: "sites/biet/faculty/p-rudreshi.jpg", alt: "P Rudreshi, Peon", width: 1024, height: 1316 },
  },
  {
    slug: "ramakrishna",
    name: "Ramakrishna",
    designation: "Peon",
    deptCode: "BT",
    deptName: "Biotechnology",
    teaching: false,
    photo: { url: "sites/biet/faculty/ramakrishna.jpg", alt: "Ramakrishna, Peon", width: 1027, height: 1294 },
  },
  {
    slug: "rudresh-k-m",
    name: "Rudresh K M",
    designation: "Peon",
    deptCode: "CV",
    deptName: "Civil Engineering",
    teaching: false,
    photo: { url: "sites/biet/faculty/rudresh-k-m.jpg", alt: "Rudresh K M, Peon", width: 960, height: 1185 },
  },
  {
    slug: "s-manjunath",
    name: "S Manjunath",
    designation: "Peon",
    deptCode: "Office",
    deptName: "Administrative Office",
    teaching: false,
    photo: { url: "sites/biet/faculty/s-manjunath.jpg", alt: "S Manjunath, Peon", width: 1484, height: 2023 },
  },
  {
    slug: "shankar-s",
    name: "Shankar S",
    designation: "Peon",
    deptCode: "EE",
    deptName: "Electrical & Electronics Engineering",
    teaching: false,
    photo: { url: "sites/biet/faculty/shankar-s.jpg", alt: "Shankar S, Peon", width: 1941, height: 2560 },
  },
  {
    slug: "shanmukha-n",
    name: "Shanmukha N",
    designation: "Peon",
    deptCode: "AI",
    deptName: "AI & Machine Learning",
    teaching: false,
    photo: { url: "sites/biet/faculty/shanmukha-n.jpg", alt: "Shanmukha   N, Peon", width: 1003, height: 1282 },
  },
  {
    slug: "shivakumar-r",
    name: "Shivakumar R",
    designation: "Peon",
    deptCode: "Library",
    deptName: "Library",
    teaching: false,
    photo: { url: "sites/biet/faculty/shivakumar-r.jpg", alt: "Shivakumar R, Peon", width: 467, height: 603 },
  },
  {
    slug: "syfuddin",
    name: "Syfuddin",
    designation: "Peon",
    deptCode: "IS",
    deptName: "Information Science & Engineering",
    teaching: false,
    photo: { url: "sites/biet/faculty/syfuddin.jpg", alt: "Syfuddin, Peon", width: 780, height: 1020 },
  },
  {
    slug: "tayappa-f",
    name: "Tayappa  F",
    designation: "Peon",
    deptCode: "TX",
    deptName: "Textile Technology",
    teaching: false,
    photo: { url: "sites/biet/faculty/tayappa-f.jpg", alt: "Tayappa  F, Peon", width: 854, height: 1238 },
  },
  {
    slug: "nagaraj-r",
    name: "Nagaraj R",
    designation: "Peon",
    deptCode: "CD",
    deptName: "Computer Science & Engineering (Data Science)",
    teaching: false,
    photo: { url: "sites/biet/faculty/nagaraj-r.jpg", alt: "Nagaraj  R, Peon", width: 1800, height: 2313 },
  },
  {
    slug: "yuvaraj-o",
    name: "Yuvaraj O",
    designation: "Peon",
    deptCode: "EE",
    deptName: "Electrical & Electronics Engineering",
    teaching: false,
    photo: { url: "sites/biet/faculty/yuvaraj-o.jpg", alt: "Yuvaraj O, Peon", width: 488, height: 617 },
  },
  {
    slug: "h-haleshi",
    name: "H Haleshi",
    designation: "Helper",
    deptCode: "ME",
    deptName: "Mechanical Engineering",
    teaching: false,
    photo: { url: "sites/biet/faculty/h-haleshi.jpg", alt: "H Haleshi, Helper", width: 1470, height: 1889 },
  },
  {
    slug: "mahalingappa-h-m",
    name: "Mahalingappa H M",
    designation: "Scavenger",
    deptCode: "Mantainances",
    deptName: "Maintenance",
    teaching: false,
    photo: { url: "sites/biet/faculty/mahalingappa-h-m.jpg", alt: "Mahalingappa H M, Scavenger", width: 1020, height: 1272 },
  },
  {
    slug: "peddanna",
    name: "Peddanna",
    designation: "Scavenger",
    deptCode: "Mantainances",
    deptName: "Maintenance",
    teaching: false,
    photo: { url: "sites/biet/faculty/peddanna.jpg", alt: "Peddanna, Scavenger", width: 571, height: 1280 },
  },
  {
    slug: "shivamurthy-h",
    name: "Shivamurthy H",
    designation: "Scavenger",
    deptCode: "Mantainances",
    deptName: "Maintenance",
    teaching: false,
    photo: { url: "sites/biet/faculty/shivamurthy-h.jpg", alt: "Shivamurthy H, Scavenger", width: 357, height: 437 },
  },
];

export const TEACHING_STAFF = STAFF.filter((s) => s.teaching);
export const NON_TEACHING_STAFF = STAFF.filter((s) => !s.teaching);

const BY_SLUG = new Map(STAFF.map((s) => [s.slug, s]));

/**
 * Look a person up by slug. Returns undefined for anyone not in the record —
 * this file carries only the rows whose photo was found, so a name printed in
 * a departmental return may legitimately have no entry here.
 */
export function getStaffMember(slug: string): StaffMember | undefined {
  return BY_SLUG.get(slug);
}

/** Support units, as opposed to academic programmes. */
const SUPPORT_UNITS = new Set<string>(["Library", "Mantainances", "Office", "SDC", "Sports"]);

export interface StaffDept {
  code: string;
  name: string;
  count: number;
  group: "program" | "other";
}

/**
 * Departments present in a roster: academic programmes first, then support
 * units, each block ordered by headcount. Factored out of STAFF_DEPTS below
 * so the merged (staff.ts + published-content) roster on the Faculty page
 * can compute the same summary over its own list instead of trusting a
 * count that was only ever accurate for the un-merged STAFF array.
 */
export function computeStaffDepts(list: Array<Pick<StaffMember, "deptCode" | "deptName">>): StaffDept[] {
  const seen = new Map<string, StaffDept>();
  for (const s of list) {
    if (!s.deptCode) continue;
    const e = seen.get(s.deptCode);
    if (e) { e.count += 1; continue; }
    seen.set(s.deptCode, {
      code: s.deptCode,
      name: s.deptName || s.deptCode,
      count: 1,
      group: SUPPORT_UNITS.has(s.deptCode) ? "other" : "program",
    });
  }
  return [...seen.values()].sort((a, b) =>
    a.group === b.group ? b.count - a.count : a.group === "program" ? -1 : 1,
  );
}

/**
 * Departments present in the roster: academic programmes first, then support
 * units, each block ordered by headcount.
 */
export const STAFF_DEPTS: StaffDept[] = computeStaffDepts(STAFF);
